/* eslint-disable react/prop-types */
/**
 * ConformIMF - Module 2 : Gestion de la Conformité
 */
import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as CJSTooltip,
  Legend as CJSLegend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as CJSTitle,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  CJSTooltip,
  CJSLegend,
  CategoryScale,
  LinearScale,
  BarElement,
  CJSTitle
);

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DataTable from "examples/Tables/DataTable";
import api from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";
import { downloadCSV } from "../../utils/exportCSV";
import { fadeInDown, fadeInUp, stagger } from "../../utils/animations";
import { StatutChip, AnalyseChip, EcartBadge } from "../../utils/conformiteChips";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const WRITE_ROLES = ["ADMIN_INSTITUTION", "GESTIONNAIRE"];

const TAB_LABELS = [
  { label: "Procédures internes", icon: "description" },
  { label: "Référentiel", icon: "fact_check" },
  { label: "Cartographie", icon: "map" },
  { label: "Plans d'action", icon: "assignment_turned_in" },
  { label: "Tableau de bord", icon: "bar_chart" },
  { label: "Reporting", icon: "print" },
];

function groupRisques(risques, avecFacteurs = false) {
  const map = {};
  risques.forEach((r) => {
    const domaine = r.domaine?.nom || "Sans domaine";
    const categorie = avecFacteurs
      ? r.facteur_risque || "Non classifié"
      : r.famille?.nom || "Sans famille";
    if (!map[domaine]) map[domaine] = {};
    if (!map[domaine][categorie]) map[domaine][categorie] = [];
    map[domaine][categorie].push(r);
  });
  return Object.entries(map).map(([domaine, cats]) => ({
    domaine,
    categories: Object.entries(cats).map(([categorie, list]) => ({ categorie, list })),
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers visuels
// ─────────────────────────────────────────────────────────────────────────────

function canWrite(user) {
  if (!user) return false;
  const allRoles = [user.profile?.role, ...(user.profile?.all_roles || [])];
  return WRITE_ROLES.some((r) => allRoles.includes(r));
}

function NiveauChip({ niveau, niveaux = [] }) {
  if (!niveau) return null;
  const found = niveaux.find((n) => n.nom === niveau);
  const cfg = found
    ? { bg: found.couleur + "22", color: found.couleur }
    : { bg: "#f5f5f5", color: "#757575" };
  return (
    <Chip
      label={niveau}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "0.7rem" }}
    />
  );
}

function StatutPlanChip({ statut }) {
  const cfg = {
    EN_COURS: { bg: "#e3f2fd", color: "#1565c0", label: "En cours" },
    REALISE: { bg: "#e8f5e9", color: "#2e7d32", label: "Réalisé" },
    REPORTE: { bg: "#f5f5f5", color: "#757575", label: "Reporté" },
  }[statut] || { bg: "#f5f5f5", color: "#555", label: statut };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "0.68rem" }}
    />
  );
}

function ScoreButtons({ label, value, onChange, disabled, labels = [], colors = [] }) {
  const fallback = ["#4caf50", "#8bc34a", "#f9a825", "#ff9800", "#f44336"];
  const n = Math.max(labels.length, colors.length, 5);
  return (
    <MDBox>
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </MDTypography>
      <MDBox display="flex" flexDirection="column" gap={0.4}>
        {Array.from({ length: n }, (_, i) => i + 1).map((v) => {
          const color = colors[v - 1] || fallback[v - 1] || "#9e9e9e";
          const lbl = labels[v - 1] || String(v);
          const selected = value === v;
          return (
            <MDBox
              key={v}
              component="button"
              type="button"
              onClick={() => !disabled && onChange(selected ? null : v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.7,
                border: `1.5px solid ${selected ? color : "#E0E6F0"}`,
                borderRadius: "6px",
                bgcolor: selected ? color + "18" : "transparent",
                cursor: disabled ? "default" : "pointer",
                transition: "all 0.15s",
                textAlign: "left",
                width: "100%",
                "&:hover": !disabled ? { borderColor: color, bgcolor: color + "10" } : {},
              }}
            >
              <MDBox
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  bgcolor: color,
                  flexShrink: 0,
                  boxShadow: selected ? `0 0 0 3px ${color}33` : "none",
                }}
              />
              <MDTypography
                variant="caption"
                sx={{
                  fontWeight: selected ? 700 : 400,
                  color: selected ? color : "text.secondary",
                  fontSize: "0.8rem",
                  flex: 1,
                }}
              >
                {lbl}
              </MDTypography>
              {selected && (
                <Icon sx={{ fontSize: "15px !important", color, flexShrink: 0 }}>check</Icon>
              )}
            </MDBox>
          );
        })}
      </MDBox>
    </MDBox>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function ConformiteContent({ initialTab, skipFirstTabs = 0, institutionId } = {}) {
  const { user } = useContext(AuthContext);
  const hasWrite = canWrite(user);

  // Ref mutable pour accéder aux handlers courants depuis des colonnes stables
  const rh = useRef({});
  const doughnutRef = useRef(null);
  const barDomaineRef = useRef(null);
  const familleChartRefs = useRef({});
  const facteurChartRefs = useRef({});
  const configLoadedRef = useRef(false);

  const [configRisque, setConfigRisque] = useState({
    labels_adequation: [],
    labels_efficacite: [],
    nb_niveaux_dmr: 5,
  });
  const [niveauxCriticite, setNiveauxCriticite] = useState([]);

  // ── Nom de l'institution (pour l'en-tête des rapports imprimés) ─────────
  const [institutionNom, setInstitutionNom] = useState("");
  useEffect(() => {
    if (institutionId) {
      api
        .get(`/api/institutions/${institutionId}`)
        .then((r) => setInstitutionNom(r.data?.nom || ""))
        .catch(() => setInstitutionNom(""));
    } else {
      setInstitutionNom(user?.profile?.roles_details?.[0]?.entite?.nom || "");
    }
  }, [institutionId, user]);

  // Colonnes stables du registre risques (useMemo vide - handlers via ref)
  const risqueColumns = useMemo(
    () => [
      {
        Header: "Code / Risque",
        accessor: "titre",
        width: "36%",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <MDBox sx={{ py: 0.25 }}>
              {r.code && (
                <Chip
                  label={r.code}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.65rem", height: 18, mb: 0.5 }}
                />
              )}
              <MDTypography
                variant="caption"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.45,
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                {r.titre}
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={0.3} mt={0.25}>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <Icon sx={{ fontSize: "12px !important", color: "#9e9e9e", flexShrink: 0 }}>
                    domain
                  </Icon>
                  <MDTypography variant="caption" color="text" sx={{ fontSize: "0.67rem" }}>
                    {r.domaine?.nom || "-"}
                  </MDTypography>
                </MDBox>
                {r.famille && (
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Icon sx={{ fontSize: "12px !important", color: "#9e9e9e", flexShrink: 0 }}>
                      folder
                    </Icon>
                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.67rem" }}>
                      {r.famille.nom}
                    </MDTypography>
                  </MDBox>
                )}
                {r.exigence?.reference && (
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Icon sx={{ fontSize: "12px !important", color: "#9e9e9e", flexShrink: 0 }}>
                      tag
                    </Icon>
                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.67rem" }}>
                      {r.exigence.reference}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          );
        },
      },
      {
        Header: "Évaluation des contrôles",
        accessor: "risque_inherent",
        width: "24%",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <MDBox sx={{ minWidth: 160 }}>
              {r.probabilite != null && r.impact != null && (
                <MDBox display="flex" alignItems="flex-start" gap={0.5} mb={0.5}>
                  <Icon
                    sx={{ fontSize: "13px !important", color: "#0A3475", flexShrink: 0, mt: "2px" }}
                  >
                    trending_up
                  </Icon>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.67rem", display: "block", wordBreak: "break-word" }}
                    >
                      <span style={{ color: "#555", fontWeight: 600 }}>Impact :</span>{" "}
                      {(configRisque.labels_impact || [])[r.impact - 1] || `Niv. ${r.impact}`}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.67rem", display: "block", wordBreak: "break-word" }}
                    >
                      <span style={{ color: "#555", fontWeight: 600 }}>Probabilité :</span>{" "}
                      {(configRisque.labels_probabilite || [])[r.probabilite - 1] ||
                        `Niv. ${r.probabilite}`}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )}
              {r.qualite_controles != null && r.efficacite_operationnelle != null && (
                <MDBox display="flex" alignItems="flex-start" gap={0.5}>
                  <Icon
                    sx={{
                      fontSize: "13px !important",
                      color: "#186A81",
                      flexShrink: 0,
                      mt: "2px",
                    }}
                  >
                    shield
                  </Icon>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.67rem", display: "block", wordBreak: "break-word" }}
                    >
                      <span style={{ color: "#555", fontWeight: 600 }}>Qualité :</span>{" "}
                      {(configRisque.labels_adequation || [])[r.qualite_controles - 1] ||
                        `Niv. ${r.qualite_controles}`}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.67rem", display: "block", wordBreak: "break-word" }}
                    >
                      <span style={{ color: "#555", fontWeight: 600 }}>Efficacité :</span>{" "}
                      {(configRisque.labels_efficacite || [])[r.efficacite_operationnelle - 1] ||
                        `Niv. ${r.efficacite_operationnelle}`}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Niveau",
        accessor: "niveau_criticite",
        width: "10%",
        Cell: ({ row }) => (
          <NiveauChip niveau={row.original.niveau_criticite} niveaux={niveauxCriticite} />
        ),
      },
      {
        Header: "Plans",
        accessor: "plans_action",
        width: "12%",
        Cell: ({ row }) => {
          const r = row.original;
          const plans = r.plans_action || [];
          const today = new Date();
          const overdueCount = plans.filter(
            (p) => p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < today
          ).length;
          return (
            <MDBox>
              <Chip
                icon={<Icon sx={{ fontSize: "12px !important" }}>assignment</Icon>}
                label={plans.length}
                size="small"
                onClick={() => rh.current.setDialogPlansView(r)}
                sx={{
                  cursor: "pointer",
                  bgcolor: plans.length ? "#e3f2fd" : "#f5f5f5",
                  color: plans.length ? "#1565c0" : "#757575",
                  fontWeight: 600,
                  fontSize: "0.68rem",
                  "&:hover": { bgcolor: plans.length ? "#bbdefb" : "#eeeeee" },
                }}
              />
              {overdueCount > 0 && (
                <MDTypography
                  variant="caption"
                  sx={{ color: "#c62828", display: "block", mt: 0.3, fontSize: "0.67rem" }}
                >
                  ⚠️ {overdueCount} retard
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "id",
        width: "10%",
        Cell: ({ row }) => {
          const r = row.original;
          return rh.current.hasWrite ? (
            <MDBox display="flex" gap={0.25}>
              <Tooltip title="Évaluer les contrôles">
                <IconButton
                  size="small"
                  onClick={() => rh.current.openEditRisque(r)}
                  sx={{ color: "#0A3475" }}
                >
                  <Icon sx={{ fontSize: "0.9rem" }}>assessment</Icon>
                </IconButton>
              </Tooltip>
              <Tooltip title="Supprimer">
                <IconButton
                  size="small"
                  onClick={() => rh.current.deleteRisque(r)}
                  sx={{ color: "#d32f2f" }}
                >
                  <Icon sx={{ fontSize: "0.9rem" }}>delete</Icon>
                </IconButton>
              </Tooltip>
            </MDBox>
          ) : null;
        },
      },
    ],
    [configRisque, niveauxCriticite]
  );

  // ── État global ─────────────────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab !== undefined) return initialTab;
    const t = parseInt(searchParams.get("tab"), 10);
    if (isNaN(t) || t < skipFirstTabs || t >= TAB_LABELS.length) return skipFirstTabs;
    return t;
  });

  const visibleTabs = TAB_LABELS.slice(skipFirstTabs);
  const [selectedDomaine, setSelectedDomaine] = useState("");
  const [domaines, setDomaines] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });

  // ── État onglet A : Procédures ──────────────────────────────────────────
  const [procedures, setProcedures] = useState([]);
  const [loadingProc, setLoadingProc] = useState(false);
  const [filterTitre, setFilterTitre] = useState("");
  const filterTitreRef = useRef(filterTitre);
  filterTitreRef.current = filterTitre;
  const [showArchived, setShowArchived] = useState(false);

  const [dialogProc, setDialogProc] = useState(false);
  const [editingProc, setEditingProc] = useState(null);
  const [formProc, setFormProc] = useState({
    titre: "",
    resume: "",
    date_adoption: "",
    domaine_id: "",
  });
  const [fileProc, setFileProc] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  const [dialogVersions, setDialogVersions] = useState(false);
  const [versionsProc, setVersionsProc] = useState(null);

  const [dialogViewer, setDialogViewer] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [viewerTitre, setViewerTitre] = useState("");
  const viewerUrlRef = useRef("");
  viewerUrlRef.current = viewerUrl;
  useEffect(
    () => () => {
      if (viewerUrlRef.current) URL.revokeObjectURL(viewerUrlRef.current);
    },
    []
  );

  // ── État onglet B : Référentiel ─────────────────────────────────────────
  const [referentiel, setReferentiel] = useState([]);
  const [loadingRef, setLoadingRef] = useState(false);
  const [filterEcart, setFilterEcart] = useState(false);
  const [filterSansProcedure, setFilterSansProcedure] = useState(false);
  const [filterCategorieRef, setFilterCategorieRef] = useState("");

  const [dialogRef, setDialogRef] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  const [formRef, setFormRef] = useState({
    exigence_id: "",
    procedure_id: "",
    code: "",
    categorie: "",
    ecart_procedure: false,
    observations: "",
  });
  const [savingRef, setSavingRef] = useState(false);
  const [exigences, setExigences] = useState([]);
  const [loadingExigences, setLoadingExigences] = useState(false);
  const [dialogDomaineRef, setDialogDomaineRef] = useState("");

  // ── État onglet C : Cartographie ────────────────────────────────────────
  const [cartographies, setCartographies] = useState([]);
  const [selectedCarto, setSelectedCarto] = useState("");
  const [risques, setRisques] = useState([]);
  const [loadingRisques, setLoadingRisques] = useState(false);
  const [filterNiveau, setFilterNiveau] = useState("");
  const [expandedRisques, setExpandedRisques] = useState(new Set());

  const [dialogRisque, setDialogRisque] = useState(false);
  const [editingRisque, setEditingRisque] = useState(null);
  const [familles, setFamilles] = useState([]);
  const [formRisque, setFormRisque] = useState({
    cartographie_id: "",
    domaine_id: "",
    famille_id: "",
    exigence_id: "",
    code: "",
    titre: "",
    probabilite: null,
    impact: null,
    qualite_controles: null,
    efficacite_operationnelle: null,
  });
  const [savingRisque, setSavingRisque] = useState(false);

  const [dialogPlan, setDialogPlan] = useState(false);
  const [planRisque, setPlanRisque] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formPlan, setFormPlan] = useState({
    action: "",
    responsable: "",
    echeance: "",
    statut: "EN_COURS",
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const [dialogPlansView, setDialogPlansView] = useState(null);

  // ── État onglet D : Plans d'action ─────────────────────────────────────
  const [filterPlanCarto, setFilterPlanCarto] = useState("");
  const [filterPlanStatut, setFilterPlanStatut] = useState("");
  const [filterPlanRetard, setFilterPlanRetard] = useState(false);

  // ── État onglet E : Tableau de bord ────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // ── État onglet F : Reporting ───────────────────────────────────────────
  const [reportRisques, setReportRisques] = useState([]);
  const [reportStats, setReportStats] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedCartoReport, setSelectedCartoReport] = useState("");
  const [selectedDomaineReport, setSelectedDomaineReport] = useState("");

  // ── Chargement initial + rechargement quand institutionId change ────────
  useEffect(() => {
    const q = institutionId ? `?institution_id=${institutionId}` : "";
    api
      .get("/api/domaines/")
      .then((r) => setDomaines(r.data))
      .catch(() => {});
    api
      .get(`/api/risques/cartographies${q}`)
      .then((r) => setCartographies(r.data))
      .catch(() => {});
    api
      .get(`/api/risques/familles${q}`)
      .then((r) => setFamilles(r.data))
      .catch(() => {});
    api
      .get(`/api/risques/config${q}`)
      .then((r) => {
        setConfigRisque(r.data);
        configLoadedRef.current = true;
      })
      .catch(() => {});
    api
      .get(`/api/risques/niveaux-criticite${q}`)
      .then((r) => setNiveauxCriticite(r.data))
      .catch(() => {});
  }, [institutionId]);

  // ── Chargement procédures ───────────────────────────────────────────────
  const loadProcedures = useCallback(() => {
    setLoadingProc(true);
    const params = new URLSearchParams();
    if (selectedDomaine) params.append("domaine_id", selectedDomaine);
    if (filterTitreRef.current.trim().length >= 2)
      params.append("titre", filterTitreRef.current.trim());
    if (showArchived) params.append("include_archived", "true");
    api
      .get(`/api/conformite/procedures?${params}`)
      .then((r) => setProcedures(r.data))
      .catch(() => showSnack("Erreur lors du chargement des procédures.", "error"))
      .finally(() => setLoadingProc(false));
  }, [selectedDomaine, showArchived, showSnack]);

  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  useEffect(() => {
    const t = setTimeout(loadProcedures, 300);
    return () => clearTimeout(t);
  }, [filterTitre]); // déclenche uniquement sur la frappe (debounce)

  // ── Chargement référentiel ──────────────────────────────────────────────
  const loadReferentiel = useCallback(() => {
    setLoadingRef(true);
    const params = new URLSearchParams();
    if (selectedDomaine) params.append("domaine_id", selectedDomaine);
    if (filterCategorieRef.trim()) params.append("categorie", filterCategorieRef.trim());
    if (filterEcart) params.append("ecart", "true");
    if (filterSansProcedure) params.append("sans_procedure", "true");
    api
      .get(`/api/conformite/referentiel?${params}`)
      .then((r) => setReferentiel(r.data))
      .catch(() => showSnack("Erreur lors du chargement du référentiel.", "error"))
      .finally(() => setLoadingRef(false));
  }, [selectedDomaine, filterCategorieRef, filterEcart, filterSansProcedure, showSnack]);

  useEffect(() => {
    if (activeTab === 1) loadReferentiel();
  }, [activeTab, loadReferentiel]);

  // ── Chargement exigences (sélecteur formulaire B) ────────────────────────
  const loadExigences = useCallback(
    (domaineOverride) => {
      setLoadingExigences(true);
      const params = new URLSearchParams();
      const dom = domaineOverride !== undefined ? domaineOverride : selectedDomaine;
      if (dom) params.append("domaine_id", dom);
      api
        .get(`/api/conformite/exigences-disponibles?${params}`)
        .then((r) => setExigences(r.data))
        .catch(() => {})
        .finally(() => setLoadingExigences(false));
    },
    [selectedDomaine]
  );

  // ── Chargement risques conformité ───────────────────────────────────────
  const loadRisques = useCallback(() => {
    setLoadingRisques(true);
    const params = new URLSearchParams();
    if (selectedCarto) params.append("cartographie_id", selectedCarto);
    if (selectedDomaine) params.append("domaine_id", selectedDomaine);
    if (filterNiveau) params.append("niveau", filterNiveau);
    if (institutionId) params.append("institution_id", institutionId);
    api
      .get(`/api/conformite/risques-conformite?${params}`)
      .then((r) => {
        setRisques(r.data);
        if (!configLoadedRef.current && r.data.length > 0 && r.data[0].institution_id) {
          api
            .get(`/api/risques/config?institution_id=${r.data[0].institution_id}`)
            .then((cfg) => {
              setConfigRisque(cfg.data);
              configLoadedRef.current = true;
            })
            .catch(() => {});
        }
      })
      .catch(() => showSnack("Erreur lors du chargement des risques.", "error"))
      .finally(() => setLoadingRisques(false));
  }, [selectedCarto, selectedDomaine, filterNiveau, institutionId, showSnack]);

  useEffect(() => {
    if (activeTab === 2 || activeTab === 3) loadRisques();
  }, [activeTab, loadRisques]);

  // ── Chargement statistiques tableau de bord ─────────────────────────────
  const loadStats = useCallback(() => {
    setLoadingStats(true);
    const params = new URLSearchParams();
    if (selectedCarto) params.append("cartographie_id", selectedCarto);
    if (selectedDomaine) params.append("domaine_id", selectedDomaine);
    if (institutionId) params.append("institution_id", institutionId);
    api
      .get(`/api/conformite/risques-conformite/statistiques?${params}`)
      .then((r) => setStats(r.data))
      .catch(() => showSnack("Erreur lors du chargement des statistiques.", "error"))
      .finally(() => setLoadingStats(false));
  }, [selectedCarto, selectedDomaine, institutionId, showSnack]);

  useEffect(() => {
    if (activeTab === 4) loadStats();
  }, [activeTab, loadStats]);

  // ── Chargement données rapport ──────────────────────────────────────────
  async function loadReportData() {
    if (!selectedCartoReport) {
      showSnack("Sélectionner une cartographie pour générer le rapport.", "warning");
      return;
    }
    setReportLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("cartographie_id", selectedCartoReport);
      if (selectedDomaineReport) params.append("domaine_id", selectedDomaineReport);
      const [rRes, sRes] = await Promise.all([
        api.get(`/api/conformite/risques-conformite?${params}`),
        api.get(`/api/conformite/risques-conformite/statistiques?${params}`),
      ]);
      setReportRisques(rRes.data);
      setReportStats(sRes.data);
    } catch {
      showSnack("Erreur lors du chargement des données du rapport.", "error");
    } finally {
      setReportLoading(false);
    }
  }

  // ── CRUD Procédures ─────────────────────────────────────────────────────

  function openCreateProc() {
    setEditingProc(null);
    setFormProc({ titre: "", resume: "", date_adoption: "", domaine_id: "" });
    setFileProc(null);
    setUploadProgress(0);
    setDialogProc(true);
  }

  function openEditProc(proc) {
    setEditingProc(proc);
    setFormProc({
      titre: proc.titre || "",
      resume: proc.resume || "",
      date_adoption: proc.date_adoption || "",
      domaine_id: proc.domaine_id || "",
    });
    setFileProc(null);
    setUploadProgress(0);
    setDialogProc(true);
  }

  async function saveProc() {
    if (!formProc.titre.trim()) {
      showSnack("Le titre est obligatoire.", "warning");
      return;
    }
    if (!editingProc && !fileProc) {
      showSnack("Un fichier PDF est obligatoire à la création.", "warning");
      return;
    }
    const fd = new FormData();
    fd.append("titre", formProc.titre.trim());
    if (formProc.resume) fd.append("resume", formProc.resume);
    if (formProc.date_adoption) fd.append("date_adoption", formProc.date_adoption);
    if (formProc.domaine_id) fd.append("domaine_id", formProc.domaine_id);
    if (fileProc) fd.append("file", fileProc);
    setSaving(true);
    setUploadProgress(0);
    try {
      if (editingProc) {
        await api.put(`/api/conformite/procedures/${editingProc.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
        });
        showSnack("Procédure mise à jour.");
      } else {
        await api.post("/api/conformite/procedures", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
        });
        showSnack("Procédure créée avec succès.");
      }
      setDialogProc(false);
      loadProcedures();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function archiveProc(proc) {
    openConfirm(`Archiver « ${proc.titre} » ?`, async () => {
      try {
        await api.delete(`/api/conformite/procedures/${proc.id}`);
        showSnack("Procédure archivée.");
        loadProcedures();
      } catch {
        showSnack("Erreur lors de l'archivage.", "error");
      }
    });
  }

  function openVersions(proc) {
    setVersionsProc(proc);
    setDialogVersions(true);
  }

  async function toggleVersion(procId, versionId) {
    try {
      await api.patch(`/api/conformite/procedures/${procId}/versions/${versionId}/toggle`);
      const updated = await api.get(`/api/conformite/procedures?include_archived=${showArchived}`);
      const proc = updated.data.find((p) => p.id === procId);
      if (proc) setVersionsProc(proc);
      loadProcedures();
    } catch {
      showSnack("Erreur lors de la mise à jour de la version.", "error");
    }
  }

  async function openViewer(proc, versionId) {
    const query = versionId ? `?version_id=${versionId}` : "";
    try {
      const response = await api.get(`/api/conformite/procedures/${proc.id}/viewer${query}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
      setViewerUrl(blobUrl);
      setViewerTitre(proc.titre);
      setDialogViewer(true);
    } catch {
      showSnack("Impossible de charger le PDF.", "error");
    }
  }

  const exportRefCSV = () => {
    const cols = [
      { key: "code", label: "Code" },
      { key: "texte_titre", label: "Texte" },
      { key: "domaine_nom", label: "Domaine" },
      { key: "categorie", label: "Catégorie" },
      { key: "ecart_procedure", label: "Écart" },
      { key: "observations", label: "Observations" },
    ];
    const rows = referentiel.map((r) => ({
      ...r,
      code: r.code || "-",
      ecart_procedure: r.ecart_procedure ? "Oui" : "Non",
    }));
    downloadCSV("referentiel_conformite", rows, cols);
  };

  // ── CRUD Référentiel ────────────────────────────────────────────────────

  function openCreateRef() {
    setDialogDomaineRef("");
    loadExigences("");
    setEditingRef(null);
    setFormRef({
      exigence_id: "",
      procedure_id: "",
      code: "",
      categorie: "",
      ecart_procedure: false,
      observations: "",
    });
    setDialogRef(true);
  }

  function openEditRef(ligne) {
    const matchedDom = domaines.find((d) => d.nom === ligne.categorie);
    const domId = matchedDom ? matchedDom.id : "";
    setDialogDomaineRef(domId);
    loadExigences(domId);
    setEditingRef(ligne);
    setFormRef({
      exigence_id: ligne.exigence_id || "",
      procedure_id: ligne.procedure_id || "",
      code: ligne.code || "",
      categorie: ligne.categorie || "",
      ecart_procedure: ligne.ecart_procedure || false,
      observations: ligne.observations || "",
    });
    setDialogRef(true);
  }

  async function saveRef() {
    if (!formRef.exigence_id) {
      showSnack("L'exigence est obligatoire.", "warning");
      return;
    }
    const payload = {
      exigence_id: Number(formRef.exigence_id),
      procedure_id: formRef.procedure_id ? Number(formRef.procedure_id) : null,
      code: formRef.code || null,
      categorie: domaines.find((d) => d.id === Number(dialogDomaineRef))?.nom || null,
      ecart_procedure: formRef.ecart_procedure,
      observations: formRef.observations || null,
    };
    setSavingRef(true);
    try {
      if (editingRef) {
        await api.put(`/api/conformite/referentiel/${editingRef.id}`, payload);
        showSnack("Ligne mise à jour.");
      } else {
        await api.post("/api/conformite/referentiel", payload);
        showSnack("Ligne ajoutée au référentiel.");
      }
      setDialogRef(false);
      loadReferentiel();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingRef(false);
    }
  }

  async function deleteRef(ligne) {
    openConfirm("Supprimer cette ligne du référentiel ?", async () => {
      try {
        await api.delete(`/api/conformite/referentiel/${ligne.id}`);
        showSnack("Ligne supprimée.");
        loadReferentiel();
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  // ── CRUD Risques conformité ─────────────────────────────────────────────

  function openEditRisque(r) {
    loadExigences();
    setEditingRisque(r);
    setFormRisque({
      cartographie_id: r.cartographie_id || "",
      domaine_id: r.domaine_id || "",
      famille_id: r.famille_id || "",
      exigence_id: r.exigence_id || "",
      code: r.code || "",
      titre: r.titre || "",
      probabilite: r.probabilite ?? null,
      impact: r.impact ?? null,
      qualite_controles: r.qualite_controles ?? null,
      efficacite_operationnelle: r.efficacite_operationnelle ?? null,
    });
    setDialogRisque(true);
  }

  // Alias lecture seule - plus de création depuis conformité

  async function saveRisque() {
    if (!editingRisque) return;
    const payload = {
      exigence_id: formRisque.exigence_id ? Number(formRisque.exigence_id) : null,
      qualite_controles: formRisque.qualite_controles,
      efficacite_operationnelle: formRisque.efficacite_operationnelle,
    };
    setSavingRisque(true);
    try {
      await api.put(`/api/conformite/risques-conformite/${editingRisque.id}`, payload);
      showSnack("Évaluation des contrôles mise à jour.");
      setDialogRisque(false);
      loadRisques();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingRisque(false);
    }
  }

  async function deleteRisque(r) {
    openConfirm(`Supprimer le risque « ${r.titre} » ?`, async () => {
      try {
        await api.delete(`/api/conformite/risques-conformite/${r.id}`);
        showSnack("Risque supprimé.");
        loadRisques();
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  function toggleRisqueExpanded(id) {
    setExpandedRisques((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ── CRUD Plans d'action conformité ──────────────────────────────────────

  function openCreatePlan(risque) {
    setPlanRisque(risque);
    setEditingPlan(null);
    setFormPlan({ action: "", responsable: "", echeance: "", statut: "EN_COURS" });
    setDialogPlan(true);
  }

  function openEditPlan(plan, risque) {
    setPlanRisque(risque);
    setEditingPlan(plan);
    setFormPlan({
      action: plan.action || "",
      responsable: plan.responsable || "",
      echeance: plan.echeance ? new Date(plan.echeance).toISOString().slice(0, 10) : "",
      statut: plan.statut || "EN_COURS",
    });
    setDialogPlan(true);
  }

  async function savePlan() {
    if (!formPlan.action.trim()) {
      showSnack("L'action est obligatoire.", "warning");
      return;
    }
    const payload = {
      risque_id: planRisque.id,
      action: formPlan.action.trim(),
      responsable: formPlan.responsable || null,
      echeance: formPlan.echeance || null,
      statut: formPlan.statut,
    };
    setSavingPlan(true);
    try {
      if (editingPlan) {
        await api.put(`/api/conformite/plans-conformite/${editingPlan.id}`, payload);
        showSnack("Plan mis à jour.");
      } else {
        await api.post("/api/conformite/plans-conformite", payload);
        showSnack("Plan créé.");
      }
      setDialogPlan(false);
      loadRisques();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingPlan(false);
    }
  }

  async function deletePlan(plan) {
    openConfirm("Supprimer ce plan d'action ?", async () => {
      try {
        await api.delete(`/api/conformite/plans-conformite/${plan.id}`);
        showSnack("Plan supprimé.");
        loadRisques();
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  // ── Génération suivi plans d'action ────────────────────────────────────
  function generateSuivi() {
    const today = new Date();
    // Reproduit exactement les filtres affichés dans l'onglet Plans d'action
    // (filterPlanCarto / filterPlanStatut / filterPlanRetard) afin que le
    // rapport imprimé corresponde à ce que l'utilisateur voit à l'écran.
    const filteredRisques = risques
      .filter((r) => !filterPlanCarto || String(r.cartographie_id) === String(filterPlanCarto))
      .map((r) => ({
        ...r,
        plans_action: (r.plans_action || []).filter((p) => {
          if (filterPlanStatut && p.statut !== filterPlanStatut) return false;
          if (
            filterPlanRetard &&
            !(p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < today)
          )
            return false;
          return true;
        }),
      }))
      .filter((r) => r.plans_action.length > 0);

    if (!filteredRisques.length) {
      showSnack("Aucun plan d'action à afficher dans le suivi.", "warning");
      return;
    }
    const cartoMeta = {
      titre: filterPlanCarto
        ? cartographies.find((c) => String(c.id) === String(filterPlanCarto))?.titre ||
          "Toutes les cartographies"
        : "Toutes les cartographies",
      institution: institutionNom,
    };
    const win = window.open("", "_blank");
    if (!win) {
      showSnack("Autorisez les pop-ups pour générer le suivi.", "warning");
      return;
    }
    win.document.write(
      buildSuiviConformiteHtml(filteredRisques, niveauxCriticite, configRisque, cartoMeta)
    );
    win.document.close();
  }

  // ── Génération du rapport HTML imprimable ───────────────────────────────
  function generateReport() {
    if (!reportRisques.length) {
      showSnack("Aucun risque à afficher dans le rapport.", "warning");
      return;
    }

    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    const lbl = (arr, idx) => (arr && idx != null && arr[idx - 1] ? esc(arr[idx - 1]) : "-");

    const cartoNom = esc(
      cartographies.find((c) => c.id === Number(selectedCartoReport))?.titre || "-"
    );
    const domaineNom = esc(
      domaines.find((d) => d.id === Number(selectedDomaineReport))?.nom || "Tous les domaines"
    );
    const today = new Date().toLocaleDateString("fr-FR");
    const avecFacteurs =
      cartographies.find((c) => c.id === Number(selectedCartoReport))?.avec_facteurs === true;
    const grouped = groupRisques(reportRisques, avecFacteurs);

    // Niveaux dynamiques depuis les statistiques (triés par seuil décroissant)
    const niveaux = (reportStats?.niveaux || []).slice().sort((a, b) => b.score_min - a.score_min);
    const niveauMap = Object.fromEntries(niveaux.map((n) => [n.nom, n]));

    // ── Matrice des risques résiduels ─────────────────────────────────────
    const nP = Math.max(5, ...reportRisques.map((r) => r.probabilite || 0));
    const nI = Math.max(5, ...reportRisques.map((r) => r.impact || 0));
    const pRows = Array.from({ length: nP }, (_, k) => nP - k);
    const iCols = Array.from({ length: nI }, (_, k) => k + 1);

    function getInherentLevel(score) {
      if (score === null || score === undefined) return undefined;
      const sorted = niveaux.slice().sort((a, b) => a.score_min - b.score_min);
      const s = Math.max(1, Math.ceil(score));
      if (s >= sorted[sorted.length - 1]?.score_max) return sorted[sorted.length - 1];
      return sorted.find((n) => s >= n.score_min && s <= n.score_max) || sorted[0];
    }

    let matrixRows = "";
    pRows.forEach((p) => {
      matrixRows += `<tr><td style="background:#f0f4ff;font-weight:700;text-align:center;padding:4px 6px;font-size:10px;border:1px solid #ddd">${p}</td>`;
      iCols.forEach((i) => {
        const lvl = getInherentLevel(p * i);
        const bg = lvl ? `${lvl.couleur}22` : "#f9f9f9";
        const border = lvl ? `1px solid ${lvl.couleur}55` : "1px solid #eee";
        const risks = reportRisques.filter((r) => r.probabilite === p && r.impact === i);
        const chips = risks
          .map((r) => {
            const rLvl = niveauMap[r.niveau_criticite];
            const c = rLvl ? rLvl.couleur : "#90a4ae";
            return `<span style="display:inline-block;background:${c};color:#fff;border-radius:3px;padding:1px 5px;font-size:7px;margin:1px;font-weight:700;white-space:nowrap">${esc(
              r.code?.substring(0, 5) || String(r.id)
            )}</span>`;
          })
          .join("");
        matrixRows += `<td style="background:${bg};border:${border};min-width:70px;height:52px;padding:3px;vertical-align:top">${chips}</td>`;
      });
      matrixRows += "</tr>";
    });

    const iHeaders = iCols
      .map(
        (i) =>
          `<th style="background:#186A81;color:#fff;text-align:center;padding:4px;font-size:10px;min-width:70px">Impact ${i}</th>`
      )
      .join("");

    const matrixHtml = `
<h2>Matrice des risques résiduels</h2>
<p style="font-size:9px;color:#555;margin-bottom:8px">
  Position : Probabilité × Impact (risque inhérent) - Couleur des badges : niveau résiduel
</p>
<div style="overflow-x:auto;margin-bottom:16px">
  <table style="border-collapse:collapse;font-size:9px">
    <thead>
      <tr>
        <th style="background:#f0f4ff;color:#0A3475;padding:4px 8px;border:1px solid #ddd;font-size:10px">P \\ I</th>
        ${iHeaders}
      </tr>
    </thead>
    <tbody>${matrixRows}</tbody>
  </table>
</div>`;

    let rows = "";
    grouped.forEach(({ domaine, categories }) => {
      rows += `<tr><td colspan="11" style="background:#0A3475;color:#FFD740;font-weight:700;padding:6px 12px;font-size:11px;">${esc(
        domaine
      )}</td></tr>`;
      categories.forEach(({ categorie, list }) => {
        rows += `<tr><td colspan="11" style="background:#eef2f8;font-weight:700;font-size:10px;padding:4px 12px;color:#0A3475;">${esc(
          categorie
        )}</td></tr>`;
        list.forEach((r) => {
          const plans = r.plans_action || [];
          const plansStr = plans.map((p) => esc(p.action)).join(" | ") || "-";
          const respStr =
            plans
              .map((p) => esc(p.responsable))
              .filter(Boolean)
              .join(", ") || "-";
          const echStr =
            plans
              .map((p) => (p.echeance ? new Date(p.echeance).toLocaleDateString("fr-FR") : ""))
              .filter(Boolean)
              .join(", ") || "-";
          const niv = niveauMap[r.niveau_criticite];
          const bg = niv ? `background:${niv.couleur}22;` : "";
          const txtColor = niv ? niv.couleur : "#333";
          rows += `<tr style="${bg}">
            <td>${esc(r.exigence?.reference || "-")}</td>
            <td>${esc(r.titre)}</td>
            <td>${avecFacteurs ? esc(r.facteur_risque || "-") : esc(r.famille?.nom || "-")}</td>
            <td style="text-align:center">${lbl(
              configRisque.labels_probabilite,
              r.probabilite
            )}</td>
            <td style="text-align:center">${lbl(configRisque.labels_impact, r.impact)}</td>
            <td style="text-align:center">${lbl(
              configRisque.labels_adequation,
              r.qualite_controles
            )}</td>
            <td style="text-align:center">${lbl(
              configRisque.labels_efficacite,
              r.efficacite_operationnelle
            )}</td>
            <td style="text-align:center;font-weight:700;color:${txtColor}">${
            r.niveau_criticite || "-"
          }</td>
            <td>${plansStr}</td><td>${respStr}</td><td>${echStr}</td>
          </tr>`;
        });
      });
    });

    const niveauCols = niveaux.map((n) => `<th>${esc(n.nom)}</th>`).join("");
    let summaryRows = "";
    if (reportStats) {
      Object.entries(reportStats.par_domaine).forEach(([dom, data]) => {
        const cells = niveaux
          .map(
            (n) =>
              `<td style="text-align:center;color:${n.couleur};font-weight:700">${
                data[n.nom] ?? 0
              }</td>`
          )
          .join("");
        summaryRows += `<tr>
          <td>${esc(dom)}</td>
          <td style="text-align:center">${data.total ?? 0}</td>
          ${cells}
        </tr>`;
      });
    }

    // ── Synthèse par famille de risques (masquée si avecFacteurs : voir
    // retour du porteur de projet du 16/10/2026, une cartographie LBC/FT se
    // classe uniquement par facteur de risque) ─────────────────────────────
    const statsByFamille = {};
    if (!avecFacteurs) {
      reportRisques.forEach((r) => {
        const fam = r.famille?.nom || "Sans famille";
        if (!statsByFamille[fam]) statsByFamille[fam] = { total: 0 };
        statsByFamille[fam].total++;
        if (r.niveau_criticite) {
          statsByFamille[fam][r.niveau_criticite] =
            (statsByFamille[fam][r.niveau_criticite] || 0) + 1;
        }
      });
    }
    const familleRows = Object.entries(statsByFamille)
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([fam, data]) => {
        const cells = niveaux
          .map(
            (n) =>
              `<td style="text-align:center;color:${n.couleur};font-weight:700">${
                data[n.nom] ?? 0
              }</td>`
          )
          .join("");
        return `<tr><td>${esc(fam)}</td><td style="text-align:center">${
          data.total
        }</td>${cells}</tr>`;
      })
      .join("");
    const familleSyntheseHtml =
      !avecFacteurs && familleRows
        ? `<h2>Synthèse par famille de risques</h2>
<table>
<thead>
  <tr><th>Famille</th><th style="text-align:center">Total</th>${niveauCols}</tr>
</thead>
<tbody>${familleRows}</tbody>
</table>`
        : "";

    // ── Synthèse par facteurs de risques (LBC/FT, si disponible) ────────────
    const statsByFacteur = {};
    if (avecFacteurs) {
      reportRisques.forEach((r) => {
        const fact = r.facteur_risque || "Non classifié";
        if (!statsByFacteur[fact]) statsByFacteur[fact] = { total: 0 };
        statsByFacteur[fact].total++;
        if (r.niveau_criticite) {
          statsByFacteur[fact][r.niveau_criticite] =
            (statsByFacteur[fact][r.niveau_criticite] || 0) + 1;
        }
      });
    }
    const facteurRows = Object.entries(statsByFacteur)
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([fact, data]) => {
        const cells = niveaux
          .map(
            (n) =>
              `<td style="text-align:center;color:${n.couleur};font-weight:700">${
                data[n.nom] ?? 0
              }</td>`
          )
          .join("");
        return `<tr><td>${esc(fact)}</td><td style="text-align:center">${
          data.total
        }</td>${cells}</tr>`;
      })
      .join("");
    const facteurSyntheseHtml =
      avecFacteurs && facteurRows
        ? `<h2>Synthèse par facteurs de risques</h2>
<table>
<thead>
  <tr><th>Facteur de risque</th><th style="text-align:center">Total</th>${niveauCols}</tr>
</thead>
<tbody>${facteurRows}</tbody>
</table>`
        : "";

    const totalRep = reportStats?.total ?? 0;
    const ecartRep = reportStats?.nb_ecarts_procedures ?? 0;
    const niveauKpis = niveaux
      .map((n) => {
        const count = reportStats?.par_niveau?.[n.nom] ?? 0;
        return (
          `<div class="kpi" style="border-top:3px solid ${n.couleur}">` +
          `<div class="v" style="color:${n.couleur}">${count}</div>` +
          `<div class="l">${esc(n.nom)}</div></div>`
        );
      })
      .join("");

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Cartographie conformité - ${cartoNom}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:11px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:28px;font-weight:700;line-height:1;color:#a5d6a7}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8;flex-wrap:wrap}
  .kpi{flex:1;min-width:80px;padding:14px 12px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:20px;font-weight:700;color:#0A3475}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.warn .v{color:#7b1fa2}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th{background:#1a237e;color:#fff;padding:6px 8px;text-align:left;
    font-size:9px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:5px 8px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:hover td{background:#f8faff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  @media print{
    .no-print{display:none}
    body{font-size:10px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,th,.hdr-badge{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eye">ConformIMF · Conformité</div>
  <h1>Cartographie des Risques de Conformité</h1>
  <div class="hdr-sub">${cartoNom} · Domaine : ${domaineNom}</div>
  <div class="hdr-date">Généré le ${today}</div>
  <div class="hdr-badge">
    <div class="big">${totalRep}</div>
    <div class="small">risques<br>analysés</div>
  </div>
</div>

<div class="kpi-strip">
  <div class="kpi"><div class="v">${totalRep}</div><div class="l">Total risques</div></div>
  ${niveauKpis}
  <div class="kpi warn"><div class="v">${ecartRep}</div><div class="l">Écarts procédures</div></div>
</div>

<div class="content">
${matrixHtml}

<h2>Registre détaillé des risques</h2>
<table>
<thead>
  <tr>
    <th style="width:80px">Exigence (réf.)</th>
    <th>Risque identifié</th>
    <th style="width:90px">${avecFacteurs ? "Facteur de risque" : "Famille"}</th>
    <th style="width:60px;text-align:center">Probab.</th>
    <th style="width:60px;text-align:center">Impact</th>
    <th style="width:70px;text-align:center">Qualité ctrl.</th>
    <th style="width:70px;text-align:center">Efficacité op.</th>
    <th style="width:80px;text-align:center">Niveau risque</th>
    <th style="width:150px">Actions d'amélioration</th>
    <th style="width:80px;text-align:center">Responsable</th>
    <th style="width:70px;text-align:center">Échéance</th>
  </tr>
</thead>
<tbody>${rows}</tbody>
</table>

<h2>Synthèse par domaine</h2>
<table>
<thead>
  <tr><th>Domaine</th><th style="text-align:center">Total</th>${niveauCols}</tr>
</thead>
<tbody>${summaryRows}</tbody>
</table>

${familleSyntheseHtml}

${facteurSyntheseHtml}
</div>

<div class="ftr">
  <span>Document confidentiel - ConformIMF</span>
  <strong>${today}</strong>
</div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=1200,height=850");
    if (!win) {
      showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning");
      return;
    }
    win.document.write(html);
    win.document.close();
  }

  // ── Colonnes DataTable - Procédures ────────────────────────────────────
  const proceduresColumns = useMemo(
    () => [
      {
        Header: "Procédure",
        accessor: "titre",
        width: "40%",
        Cell: ({ row }) => {
          const proc = row.original;
          return (
            <MDBox>
              <MDTypography variant="body2" fontWeight="medium">
                {proc.titre}
              </MDTypography>
              {proc.resume && (
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {proc.resume}
                </MDTypography>
              )}
              {proc.date_adoption && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Adoptée le {new Date(proc.date_adoption).toLocaleDateString("fr-FR")}
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Domaine",
        accessor: "domaine",
        width: "20%",
        Cell: ({ row }) => (
          <MDTypography variant="caption">{row.original.domaine?.nom || "-"}</MDTypography>
        ),
      },
      {
        Header: "Statut",
        accessor: "statut",
        width: "20%",
        Cell: ({ row }) => (
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            <StatutChip statut={row.original.statut} />
            <AnalyseChip analysee={row.original.est_analysee} />
          </MDBox>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "20%",
        Cell: ({ row }) => {
          const proc = row.original;
          const activeVersion = (proc.versions || [])
            .filter((v) => v.actif)
            .sort((a, b) => new Date(b.date_version) - new Date(a.date_version))[0];
          return (
            <MDBox display="flex" gap={0.5}>
              {activeVersion && (
                <>
                  <Tooltip title="Voir le PDF">
                    <IconButton
                      size="small"
                      onClick={() => openViewer(proc)}
                      sx={{ color: "#186A81" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>visibility</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Historique des versions">
                    <IconButton
                      size="small"
                      onClick={() => openVersions(proc)}
                      sx={{ color: "#555" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>history</Icon>
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {hasWrite && (
                <Tooltip title="Modifier">
                  <IconButton
                    size="small"
                    onClick={() => openEditProc(proc)}
                    sx={{ color: "#0A3475" }}
                  >
                    <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {hasWrite && proc.statut === "ACTIF" && (
                <Tooltip title="Archiver">
                  <IconButton
                    size="small"
                    onClick={() => archiveProc(proc)}
                    sx={{ color: "#d32f2f" }}
                  >
                    <Icon sx={{ fontSize: "1rem" }}>archive</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],
    [hasWrite]
  );

  // ── Colonnes DataTable - Référentiel ────────────────────────────────────
  const referentielColumns = useMemo(
    () => [
      // ── Col 1 : Exigence (code + texte + ref + catégorie) ─────────────────
      {
        Header: "Exigence",
        accessor: "exigence",
        width: "42%",
        wrap: true,
        Cell: ({ row }) => {
          const ligne = row.original;
          return (
            <MDBox>
              {ligne.code && (
                <Chip
                  label={ligne.code}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.65rem", fontWeight: 600, height: 18, mb: 0.5 }}
                />
              )}
              <MDTypography
                variant="caption"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                }}
              >
                {ligne.exigence?.exigence || "-"}
              </MDTypography>
              <MDBox display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                {ligne.exigence?.reference && (
                  <MDBox display="flex" alignItems="center" gap={0.4}>
                    <Icon sx={{ fontSize: "11px", color: "#9e9e9e" }}>tag</Icon>
                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.68rem" }}>
                      {ligne.exigence.reference}
                    </MDTypography>
                  </MDBox>
                )}
                {ligne.categorie && (
                  <MDBox display="flex" alignItems="center" gap={0.4}>
                    <Icon sx={{ fontSize: "11px", color: "#9e9e9e" }}>label</Icon>
                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.68rem" }}>
                      {ligne.categorie}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          );
        },
      },
      // ── Col 2 : Procédure + Statut ────────────────────────────────────────
      {
        Header: "Procédure / Statut",
        accessor: "procedure",
        width: "28%",
        wrap: true,
        Cell: ({ row }) => {
          const ligne = row.original;
          return (
            <MDBox>
              {ligne.procedure ? (
                <MDTypography
                  variant="caption"
                  fontWeight="medium"
                  sx={{
                    color: "#186A81",
                    cursor: "pointer",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.4,
                    "&:hover": { color: "#0A3475", textDecoration: "underline" },
                  }}
                  onClick={() => openViewer(ligne.procedure)}
                >
                  {ligne.procedure.titre}
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                  Aucune procédure liée
                </MDTypography>
              )}
              <MDBox mt={0.5}>
                <EcartBadge ligne={ligne} />
              </MDBox>
            </MDBox>
          );
        },
      },
      // ── Col 3 : Domaine + Observations ───────────────────────────────────
      {
        Header: "Domaine / Notes",
        accessor: "domaine_nom",
        width: "20%",
        wrap: true,
        Cell: ({ row }) => {
          const ligne = row.original;
          return (
            <MDBox>
              {ligne.domaine_nom ? (
                <Chip
                  label={ligne.domaine_nom}
                  size="small"
                  sx={{
                    bgcolor: "#e8edf5",
                    color: "#0A3475",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    height: 20,
                    maxWidth: "100%",
                    mb: 0.5,
                  }}
                />
              ) : null}
              {ligne.observations && (
                <Tooltip title={ligne.observations} placement="top">
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      cursor: "help",
                      fontSize: "0.68rem",
                      fontStyle: "italic",
                    }}
                  >
                    {ligne.observations}
                  </MDTypography>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
      // ── Col 4 : Actions ───────────────────────────────────────────────────
      ...(hasWrite
        ? [
            {
              Header: "Actions",
              accessor: "ref_actions",
              width: "10%",
              Cell: ({ row }) => (
                <MDBox display="flex" gap={0.5}>
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      onClick={() => openEditRef(row.original)}
                      sx={{ color: "#0A3475" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small"
                      onClick={() => deleteRef(row.original)}
                      sx={{ color: "#d32f2f" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>delete</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              ),
            },
          ]
        : []),
    ],
    [hasWrite]
  );

  // ── Rendu filtres Tab A ─────────────────────────────────────────────────

  function renderFilters() {
    return (
      <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <MDInput
          placeholder="Rechercher par titre..."
          value={filterTitre}
          onChange={(e) => setFilterTitre(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <Icon sx={{ mr: 1, color: "text.secondary", fontSize: "1.1rem" }}>search</Icon>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          <Select
            displayEmpty
            value={selectedDomaine}
            onChange={(e) => setSelectedDomaine(e.target.value)}
            sx={{ height: "45px" }}
            renderValue={(v) =>
              v ? (
                domaines.find((d) => d.id === v)?.nom
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
              )
            }
          >
            <MenuItem value="">Tous les domaines</MenuItem>
            {domaines.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.nom}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <MDBox
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => setShowArchived((v) => !v)}
        >
          <MDBox
            sx={{
              position: "relative",
              width: 36,
              height: 20,
              borderRadius: "10px",
              background: showArchived ? "linear-gradient(135deg, #0A3475, #186A81)" : "#d0d7e2",
              transition: "background 0.25s",
              flexShrink: 0,
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                top: 3,
                left: showArchived ? 19 : 3,
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </MDBox>
          <MDTypography
            variant="caption"
            sx={{
              color: showArchived ? "#0A3475" : "#888",
              fontWeight: showArchived ? 600 : 400,
              transition: "color 0.2s",
            }}
          >
            Afficher les archivées
          </MDTypography>
        </MDBox>
        {hasWrite && (
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<Icon>add</Icon>}
            onClick={openCreateProc}
            sx={{ ml: "auto", height: "45px" }}
          >
            Nouvelle procédure
          </MDButton>
        )}
      </MDBox>
    );
  }

  // ── Rendu filtres Tab B ─────────────────────────────────────────────────

  function renderFiltersRef() {
    return (
      <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <MDInput
          placeholder="Rechercher..."
          value={filterCategorieRef}
          onChange={(e) => setFilterCategorieRef(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <Icon sx={{ mr: 1, color: "text.secondary", fontSize: "1.1rem" }}>search</Icon>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          <Select
            displayEmpty
            value={selectedDomaine}
            onChange={(e) => setSelectedDomaine(e.target.value)}
            sx={{ height: "45px" }}
            renderValue={(v) =>
              v ? (
                domaines.find((d) => d.id === v)?.nom
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
              )
            }
          >
            <MenuItem value="">Tous les domaines</MenuItem>
            {domaines.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.nom}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <MDBox
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => setFilterEcart((v) => !v)}
        >
          <MDBox
            sx={{
              position: "relative",
              width: 36,
              height: 20,
              borderRadius: "10px",
              background: filterEcart ? "linear-gradient(135deg, #e65100, #fb8c00)" : "#d0d7e2",
              transition: "background 0.25s",
              flexShrink: 0,
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                top: 3,
                left: filterEcart ? 19 : 3,
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </MDBox>
          <MDTypography
            variant="caption"
            sx={{
              color: filterEcart ? "#e65100" : "#888",
              fontWeight: filterEcart ? 600 : 400,
              transition: "color 0.2s",
            }}
          >
            Écarts uniquement
          </MDTypography>
        </MDBox>
        <MDBox
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => setFilterSansProcedure((v) => !v)}
        >
          <MDBox
            sx={{
              position: "relative",
              width: 36,
              height: 20,
              borderRadius: "10px",
              background: filterSansProcedure
                ? "linear-gradient(135deg, #b71c1c, #e53935)"
                : "#d0d7e2",
              transition: "background 0.25s",
              flexShrink: 0,
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                top: 3,
                left: filterSansProcedure ? 19 : 3,
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </MDBox>
          <MDTypography
            variant="caption"
            sx={{
              color: filterSansProcedure ? "#b71c1c" : "#888",
              fontWeight: filterSansProcedure ? 600 : 400,
              transition: "color 0.2s",
            }}
          >
            Sans procédure
          </MDTypography>
        </MDBox>
        <MDButton
          variant="outlined"
          color="success"
          size="small"
          startIcon={<Icon>download</Icon>}
          onClick={exportRefCSV}
          sx={{ ml: hasWrite ? 0 : "auto", height: "45px" }}
          disabled={referentiel.length === 0}
        >
          Excel
        </MDButton>
        {hasWrite && (
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<Icon>add</Icon>}
            onClick={openCreateRef}
            sx={{ ml: "auto", height: "45px" }}
          >
            Ajouter une ligne
          </MDButton>
        )}
      </MDBox>
    );
  }

  // ── Rendu filtres Tab C ─────────────────────────────────────────────────

  function renderFiltersCartographie() {
    return (
      <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <FormControl sx={{ minWidth: 180 }}>
          <Select
            displayEmpty
            value={selectedDomaine}
            onChange={(e) => setSelectedDomaine(e.target.value)}
            sx={{ height: "45px" }}
            renderValue={(v) =>
              v ? (
                domaines.find((d) => d.id === v)?.nom
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
              )
            }
          >
            <MenuItem value="">Tous les domaines</MenuItem>
            {domaines.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.nom}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <Select
            displayEmpty
            value={selectedCarto}
            onChange={(e) => setSelectedCarto(e.target.value)}
            sx={{ height: "45px" }}
            renderValue={(v) =>
              v ? (
                cartographies.find((c) => c.id === v)?.titre
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                  Toutes les cartographies
                </span>
              )
            }
          >
            <MenuItem value="">Toutes</MenuItem>
            {cartographies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.titre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            displayEmpty
            value={filterNiveau}
            onChange={(e) => setFilterNiveau(e.target.value)}
            sx={{ height: "45px" }}
            renderValue={(v) =>
              v ? v : <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous niveaux</span>
            }
          >
            <MenuItem value="">Tous niveaux</MenuItem>
            {niveauxCriticite
              .slice()
              .sort((a, b) => b.score_min - a.score_min)
              .map((n) => (
                <MenuItem key={n.nom} value={n.nom}>
                  {n.nom}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </MDBox>
    );
  }

  // ── Rendu tableau procédures ────────────────────────────────────────────

  function renderProceduresTable() {
    if (loadingProc)
      return (
        <MDBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </MDBox>
      );
    if (!procedures.length)
      return (
        <MDBox textAlign="center" py={4}>
          <Icon sx={{ fontSize: 48, color: "text.disabled" }}>description</Icon>
          <MDTypography variant="body2" color="text">
            Aucune procédure trouvée.
          </MDTypography>
        </MDBox>
      );
    return (
      <DataTable
        table={{ columns: proceduresColumns, rows: procedures }}
        isSorted={false}
        entriesPerPage
        showTotalEntries
      />
    );
  }

  // ── Rendu tableau référentiel ───────────────────────────────────────────

  function renderReferentielTable() {
    if (loadingRef)
      return (
        <MDBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </MDBox>
      );
    if (!referentiel.length)
      return (
        <MDBox textAlign="center" py={6}>
          <Icon sx={{ fontSize: 48, color: "text.disabled" }}>fact_check</Icon>
          <MDTypography variant="body2" color="text" mt={1}>
            Aucune ligne dans le référentiel.{" "}
            {hasWrite && "Cliquez sur « Ajouter une ligne » pour commencer."}
          </MDTypography>
        </MDBox>
      );
    return (
      <MDBox>
        <DataTable
          table={{ columns: referentielColumns, rows: referentiel }}
          isSorted={false}
          entriesPerPage
          showTotalEntries
        />
        <MDBox display="flex" gap={2} mt={1} flexWrap="wrap">
          <MDTypography variant="caption" color="text">
            {referentiel.length} ligne{referentiel.length > 1 ? "s" : ""} au total
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "#c62828" }}>
            · {referentiel.filter((l) => !l.procedure_id).length} sans procédure
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "#e65100" }}>
            · {referentiel.filter((l) => l.ecart_procedure).length} écart
            {referentiel.filter((l) => l.ecart_procedure).length > 1 ? "s" : ""}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "#2e7d32" }}>
            · {referentiel.filter((l) => l.procedure_id && !l.ecart_procedure).length} conforme
            {referentiel.filter((l) => l.procedure_id && !l.ecart_procedure).length > 1 ? "s" : ""}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  }

  // ── Rendu registre des risques (Tab C) ──────────────────────────────────

  function renderPlansInline(risque) {
    const plans = risque.plans_action || [];
    const today = new Date();
    return (
      <MDBox p={2} sx={{ bgcolor: "#f8f9fa" }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <MDTypography variant="caption" fontWeight="bold" color="text">
            Plans d&apos;action ({plans.length})
          </MDTypography>
          {hasWrite && (
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              startIcon={<Icon>add</Icon>}
              onClick={() => openCreatePlan(risque)}
              sx={{ py: 0.25 }}
            >
              Ajouter
            </MDButton>
          )}
        </MDBox>
        {plans.length === 0 ? (
          <MDTypography variant="caption" color="text">
            Aucun plan pour ce risque.
          </MDTypography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#eef2f8" }}>
                {["Action", "Responsable / Échéance", "Statut", hasWrite ? "Actions" : null]
                  .filter(Boolean)
                  .map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>
                      {h}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const planEch = plan.echeance ? new Date(plan.echeance) : null;
                const soonDate = new Date(today);
                soonDate.setDate(today.getDate() + 7);
                const overdue = planEch && plan.statut !== "REALISE" && planEch < today;
                const soon = planEch && plan.statut !== "REALISE" && !overdue && planEch < soonDate;
                return (
                  <TableRow
                    key={plan.id}
                    sx={{
                      bgcolor: overdue ? "#fff3f3" : soon ? "#fffbf0" : "transparent",
                    }}
                  >
                    {/* Action - wrap naturel */}
                    <TableCell
                      sx={{
                        fontSize: "0.72rem",
                        maxWidth: 320,
                        whiteSpace: "normal",
                        lineHeight: 1.45,
                      }}
                    >
                      {plan.action}
                    </TableCell>
                    {/* Responsable + Échéance regroupés */}
                    <TableCell sx={{ minWidth: 130 }}>
                      {plan.responsable && (
                        <MDBox display="flex" alignItems="center" gap={0.4} mb={0.3}>
                          <Icon sx={{ fontSize: "11px", color: "#9e9e9e" }}>person</Icon>
                          <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                            {plan.responsable}
                          </MDTypography>
                        </MDBox>
                      )}
                      {planEch ? (
                        <MDBox display="flex" alignItems="center" gap={0.4}>
                          <Icon
                            sx={{
                              fontSize: "11px",
                              color: overdue ? "#c62828" : soon ? "#e65100" : "#9e9e9e",
                            }}
                          >
                            event
                          </Icon>
                          <MDTypography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              color: overdue ? "#c62828" : soon ? "#e65100" : "inherit",
                              fontWeight: overdue || soon ? 700 : 400,
                            }}
                          >
                            {planEch.toLocaleDateString("fr-FR")}
                            {overdue && " ⚠️"}
                            {soon && " 🔔"}
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                          Pas d&apos;échéance
                        </MDTypography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatutPlanChip statut={plan.statut} />
                    </TableCell>
                    {hasWrite && (
                      <TableCell>
                        <MDBox display="flex" gap={0.5}>
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              onClick={() => openEditPlan(plan, risque)}
                              sx={{ color: "#0A3475" }}
                            >
                              <Icon sx={{ fontSize: "0.9rem" }}>edit</Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              onClick={() => deletePlan(plan)}
                              sx={{ color: "#d32f2f" }}
                            >
                              <Icon sx={{ fontSize: "0.9rem" }}>delete</Icon>
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </MDBox>
    );
  }

  function renderRisqueRegistre() {
    rh.current = { hasWrite, openEditRisque, deleteRisque, setDialogPlansView };

    if (loadingRisques)
      return (
        <MDBox display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </MDBox>
      );
    if (!risques.length)
      return (
        <MDBox textAlign="center" py={8}>
          <Icon sx={{ fontSize: 56, color: "#ccc" }}>map</Icon>
          <MDTypography variant="h6" color="text" mt={1}>
            Aucun risque dans cette cartographie.
          </MDTypography>
          <MDTypography variant="body2" color="text" mt={0.5}>
            Les risques se créent depuis le module <strong>Gestion des Risques</strong>.
          </MDTypography>
        </MDBox>
      );

    return (
      <MDBox>
        <DataTable
          table={{ columns: risqueColumns, rows: risques }}
          isSorted={false}
          entriesPerPage={{ defaultValue: 10, entries: ["5", "10", "20", "50"] }}
          showTotalEntries
          canSearch
          noEndBorder
        />
        <MDBox display="flex" gap={2} mt={1} flexWrap="wrap">
          <MDTypography variant="caption" color="text">
            {risques.length} risque{risques.length > 1 ? "s" : ""} au total
          </MDTypography>
          {niveauxCriticite
            .slice()
            .sort((a, b) => b.score_min - a.score_min)
            .map((n) => {
              const cnt = risques.filter((r) => r.niveau_criticite === n.nom).length;
              return (
                <MDTypography key={n.nom} variant="caption" sx={{ color: n.couleur }}>
                  ·&nbsp;{cnt} {n.nom.toLowerCase()}
                  {cnt > 1 ? "s" : ""}
                </MDTypography>
              );
            })}
        </MDBox>
      </MDBox>
    );
  }

  // ── Rendu Plans d'action (Tab D) ────────────────────────────────────────

  function renderPlansTab() {
    const today = new Date();
    const allPlans = risques.flatMap((r) =>
      (r.plans_action || []).map((p) => ({ ...p, risque: r }))
    );

    let filteredPlans = allPlans;
    if (filterPlanCarto)
      filteredPlans = filteredPlans.filter(
        (p) => String(p.risque.cartographie_id) === String(filterPlanCarto)
      );
    if (filterPlanStatut)
      filteredPlans = filteredPlans.filter((p) => p.statut === filterPlanStatut);
    if (filterPlanRetard)
      filteredPlans = filteredPlans.filter(
        (p) => p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < today
      );

    // KPIs sur les plans filtrés (cohérence avec la vue affichée)
    const enCours = filteredPlans.filter((p) => p.statut === "EN_COURS").length;
    const realises = filteredPlans.filter((p) => p.statut === "REALISE").length;
    const reportes = filteredPlans.filter((p) => p.statut === "REPORTE").length;
    const enRetard = filteredPlans.filter(
      (p) => p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < today
    ).length;

    if (loadingRisques)
      return (
        <MDBox display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </MDBox>
      );

    const grouped = {};
    filteredPlans.forEach((p) => {
      const domaine = p.risque.domaine?.nom || "Sans domaine";
      const avecFacteursPlan = cartographies.find(
        (c) => c.id === p.risque.cartographie_id
      )?.avec_facteurs;
      const categorie = avecFacteursPlan
        ? p.risque.facteur_risque || "Non classifié"
        : p.risque.famille?.nom || "Sans famille";
      const rid = p.risque.id;
      if (!grouped[domaine]) grouped[domaine] = {};
      if (!grouped[domaine][categorie]) grouped[domaine][categorie] = {};
      if (!grouped[domaine][categorie][rid])
        grouped[domaine][categorie][rid] = { risque: p.risque, plans: [] };
      grouped[domaine][categorie][rid].plans.push(p);
    });

    return (
      <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
        {/* Filtres */}
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3} alignItems="center">
          <FormControl sx={{ minWidth: 220 }}>
            <Select
              displayEmpty
              value={filterPlanCarto}
              onChange={(e) => setFilterPlanCarto(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  cartographies.find((c) => String(c.id) === String(v))?.titre
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                    Toutes les cartographies
                  </span>
                )
              }
            >
              <MenuItem value="">Toutes les cartographies</MenuItem>
              {cartographies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.titre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={selectedDomaine}
              onChange={(e) => setSelectedDomaine(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  domaines.find((d) => d.id === v)?.nom
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
                )
              }
            >
              <MenuItem value="">Tous les domaines</MenuItem>
              {domaines.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <Select
              displayEmpty
              value={filterPlanStatut}
              onChange={(e) => setFilterPlanStatut(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  { EN_COURS: "En cours", REALISE: "Réalisé", REPORTE: "Reporté" }[v]
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les statuts</span>
                )
              }
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="EN_COURS">En cours</MenuItem>
              <MenuItem value="REALISE">Réalisé</MenuItem>
              <MenuItem value="REPORTE">Reporté</MenuItem>
            </Select>
          </FormControl>
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setFilterPlanRetard((v) => !v)}
          >
            <MDBox
              sx={{
                position: "relative",
                width: 36,
                height: 20,
                borderRadius: "10px",
                background: filterPlanRetard
                  ? "linear-gradient(135deg, #b71c1c, #e53935)"
                  : "#d0d7e2",
                transition: "background 0.25s",
                flexShrink: 0,
              }}
            >
              <MDBox
                sx={{
                  position: "absolute",
                  top: 3,
                  left: filterPlanRetard ? 19 : 3,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                  transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: filterPlanRetard ? "#b71c1c" : "#888",
                fontWeight: filterPlanRetard ? 600 : 400,
                transition: "color 0.2s",
              }}
            >
              Plans en retard uniquement
            </MDTypography>
          </MDBox>
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            startIcon={<Icon>refresh</Icon>}
            onClick={loadRisques}
            sx={{ height: "45px" }}
          >
            Actualiser
          </MDButton>
          <MDButton
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<Icon>track_changes</Icon>}
            onClick={generateSuivi}
            sx={{ height: "45px", ml: "auto" }}
          >
            Suivi
          </MDButton>
        </MDBox>

        {/* KPI */}
        <MDBox display="flex" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: "Total", value: filteredPlans.length, color: "#0A3475" },
            { label: "En cours", value: enCours, color: "#1565c0" },
            { label: "Réalisés", value: realises, color: "#2e7d32" },
            { label: "Reportés", value: reportes, color: "#757575" },
            { label: "En retard ⚠️", value: enRetard, color: "#c62828" },
          ].map(({ label, value, color }) => (
            <MDBox
              key={label}
              sx={{
                border: `1px solid ${color}44`,
                bgcolor: `${color}11`,
                borderRadius: 2,
                px: 2,
                py: 1,
                minWidth: 100,
                textAlign: "center",
              }}
            >
              <MDTypography variant="h4" fontWeight="bold" sx={{ color }}>
                {value}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {label}
              </MDTypography>
              {(filterPlanStatut || filterPlanRetard) && label === "Total" && (
                <MDTypography variant="caption" sx={{ color: "#9e9e9e", fontSize: "0.62rem" }}>
                  /{allPlans.length} total
                </MDTypography>
              )}
            </MDBox>
          ))}
        </MDBox>

        {filteredPlans.length === 0 ? (
          <MDBox textAlign="center" py={8}>
            <Icon sx={{ fontSize: 56, color: "#ccc" }}>assignment_turned_in</Icon>
            <MDTypography variant="h6" color="text" mt={1}>
              {allPlans.length === 0
                ? "Aucun plan d'action créé."
                : "Aucun plan ne correspond aux filtres."}
            </MDTypography>
            {allPlans.length === 0 && (
              <MDTypography variant="body2" color="text">
                Créez des risques dans l&apos;onglet Cartographie, puis ajoutez des plans depuis
                chaque risque.
              </MDTypography>
            )}
          </MDBox>
        ) : (
          Object.entries(grouped).map(([domaine, categories]) => (
            <MDBox
              key={domaine}
              mb={3}
              sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}
            >
              <MDBox
                sx={{
                  background: "linear-gradient(195deg, #0A3475, #186A81)",
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ color: "#FFD740", fontSize: "1.1rem" }}>domain</Icon>
                <MDTypography variant="subtitle2" sx={{ color: "#FFD740", fontWeight: 700 }}>
                  {domaine}
                </MDTypography>
              </MDBox>

              {Object.entries(categories).map(([categorie, risquesMap]) => (
                <MDBox key={categorie}>
                  <MDBox
                    sx={{ bgcolor: "#eef2f8", px: 2, py: 0.75, borderBottom: "1px solid #dce3ef" }}
                  >
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#0A3475" }}>
                      {categorie}
                    </MDTypography>
                  </MDBox>

                  {Object.values(risquesMap).map(({ risque: r, plans }) => (
                    <MDBox key={r.id} sx={{ borderBottom: "1px solid #f0f0f0" }}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        px={2}
                        py={1}
                        sx={{ bgcolor: "#fafbff" }}
                      >
                        <NiveauChip niveau={r.niveau_criticite} niveaux={niveauxCriticite} />
                        <MDTypography variant="caption" fontWeight="bold">
                          {r.titre}
                        </MDTypography>
                        <Chip
                          label={`${plans.length} plan${plans.length > 1 ? "s" : ""}`}
                          size="small"
                          sx={{
                            bgcolor: "#e8edf5",
                            color: "#0A3475",
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                        {r.code && (
                          <Chip
                            label={r.code}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.65rem", height: 18 }}
                          />
                        )}
                      </MDBox>

                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#f5f7fc" }}>
                              {[
                                "Action",
                                "Responsable / Échéance",
                                "Statut",
                                hasWrite ? "Actions" : null,
                              ]
                                .filter(Boolean)
                                .map((h) => (
                                  <TableCell
                                    key={h}
                                    sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}
                                  >
                                    {h}
                                  </TableCell>
                                ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {plans.map((plan) => {
                              const overdue =
                                plan.echeance &&
                                plan.statut !== "REALISE" &&
                                new Date(plan.echeance) < today;
                              return (
                                <TableRow
                                  key={plan.id}
                                  sx={{ bgcolor: overdue ? "#fff3f3" : "transparent" }}
                                >
                                  {/* Action - wrap naturel */}
                                  <TableCell
                                    sx={{
                                      fontSize: "0.72rem",
                                      maxWidth: 380,
                                      whiteSpace: "normal",
                                      lineHeight: 1.45,
                                    }}
                                  >
                                    {plan.action}
                                  </TableCell>
                                  {/* Responsable + Échéance regroupés */}
                                  <TableCell sx={{ minWidth: 130 }}>
                                    {plan.responsable && (
                                      <MDBox display="flex" alignItems="center" gap={0.4} mb={0.3}>
                                        <Icon sx={{ fontSize: "11px", color: "#9e9e9e" }}>
                                          person
                                        </Icon>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          sx={{ fontSize: "0.7rem" }}
                                        >
                                          {plan.responsable}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                    {plan.echeance ? (
                                      <MDBox display="flex" alignItems="center" gap={0.4}>
                                        <Icon
                                          sx={{
                                            fontSize: "11px",
                                            color: overdue ? "#c62828" : "#9e9e9e",
                                          }}
                                        >
                                          event
                                        </Icon>
                                        <MDTypography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.7rem",
                                            color: overdue ? "#c62828" : "inherit",
                                            fontWeight: overdue ? 700 : 400,
                                          }}
                                        >
                                          {new Date(plan.echeance).toLocaleDateString("fr-FR")}
                                          {overdue && " ⚠️"}
                                        </MDTypography>
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text"
                                        sx={{ fontSize: "0.7rem", fontStyle: "italic" }}
                                      >
                                        Pas d&apos;échéance
                                      </MDTypography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <StatutPlanChip statut={plan.statut} />
                                  </TableCell>
                                  {hasWrite && (
                                    <TableCell>
                                      <MDBox display="flex" gap={0.5}>
                                        <Tooltip title="Modifier">
                                          <IconButton
                                            size="small"
                                            onClick={() => openEditPlan(plan, r)}
                                            sx={{ color: "#0A3475" }}
                                          >
                                            <Icon sx={{ fontSize: "0.9rem" }}>edit</Icon>
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                          <IconButton
                                            size="small"
                                            onClick={() => deletePlan(plan)}
                                            sx={{ color: "#d32f2f" }}
                                          >
                                            <Icon sx={{ fontSize: "0.9rem" }}>delete</Icon>
                                          </IconButton>
                                        </Tooltip>
                                      </MDBox>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </MDBox>
                  ))}
                </MDBox>
              ))}
            </MDBox>
          ))
        )}
      </MDBox>
    );
  }

  // ── Rendu Tableau de bord (Tab E) ───────────────────────────────────────

  function renderDashboard() {
    // Une cartographie en mode "facteurs" (LBC/FT) se classe uniquement par
    // facteur de risque, jamais par famille - voir retour du porteur de
    // projet du 16/10/2026. Si aucune cartographie précise n'est
    // sélectionnée ("Toutes les cartographies"), on affiche les deux, le
    // jeu de données étant alors un mélange.
    const avecFacteursDash =
      !!selectedCarto &&
      cartographies.find((c) => c.id === Number(selectedCarto))?.avec_facteurs === true;

    function handlePrint() {
      if (!stats) return;
      const now = new Date().toLocaleString("fr-FR");
      const cartoTitre =
        cartographies.find((c) => c.id === Number(selectedCarto))?.titre ||
        "Toutes les cartographies";
      const niveaux = stats.niveaux || [];
      const esc = (s) =>
        String(s ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

      // Capture des charts en base64
      const imgDoughnut = doughnutRef.current?.toBase64Image?.() || "";
      const imgDomaine = barDomaineRef.current?.toBase64Image?.() || "";
      const imgsFamille = Object.entries(familleChartRefs.current).map(([fam, chart]) => ({
        fam,
        src: chart?.canvas ? chart.toBase64Image?.() || "" : "",
      }));
      const cartoAvecFacteurs = Object.keys(stats.par_facteur || {}).length > 0;
      const imgsFacteur = cartoAvecFacteurs
        ? Object.entries(facteurChartRefs.current).map(([fac, chart]) => ({
            fac,
            src: chart?.canvas ? chart.toBase64Image?.() || "" : "",
          }))
        : [];

      const kpiHtml = [
        `<div class="kpi"><b>${stats.total}</b><span>Total risques résiduels</span></div>`,
        ...niveaux.map(
          (n) =>
            `<div class="kpi" style="border-top:3px solid ${n.couleur}"><b style="color:${
              n.couleur
            }">${stats.par_niveau?.[n.nom] ?? 0}</b><span>${esc(n.nom)}</span></div>`
        ),
        `<div class="kpi" style="border-top:3px solid #7b1fa2"><b style="color:#7b1fa2">${stats.nb_ecarts_procedures}</b><span>Écarts de procédures</span></div>`,
      ].join("");

      const nCols = niveaux.map((n) => `<th>${esc(n.nom)}</th>`).join("");
      const domaineBody = Object.entries(stats.par_domaine || {})
        .map(([dom, data]) => {
          const cells = niveaux.map((n) => `<td>${data[n.nom] || 0}</td>`).join("");
          const tot = niveaux.reduce((s, n) => s + (data[n.nom] || 0), 0);
          return `<tr><td>${esc(dom)}</td>${cells}<td><b>${tot}</b></td></tr>`;
        })
        .join("");
      const familleBody = avecFacteursDash
        ? ""
        : Object.entries(stats.par_famille || {})
            .map(([fam, data]) => {
              const cells = niveaux.map((n) => `<td>${data[n.nom] || 0}</td>`).join("");
              return `<tr><td>${esc(fam)}</td>${cells}<td><b>${data.total || 0}</b></td></tr>`;
            })
            .join("");

      const familleSections = avecFacteursDash
        ? ""
        : imgsFamille
            .map(
              ({ fam, src }) =>
                `<div class="fam-card">
              <div class="fam-titre">${esc(fam)}</div>
              ${src ? `<img src="${src}" style="width:100%;height:auto;display:block">` : ""}
            </div>`
            )
            .join("");

      const facteurBody = cartoAvecFacteurs
        ? Object.entries(stats.par_facteur || {})
            .map(([fac, data]) => {
              const cells = niveaux.map((n) => `<td>${data[n.nom] || 0}</td>`).join("");
              return `<tr><td>${esc(fac)}</td>${cells}<td><b>${data.total || 0}</b></td></tr>`;
            })
            .join("")
        : "";
      const facteurSections = imgsFacteur
        .map(
          ({ fac, src }) =>
            `<div class="fam-card" style="border-color:#186A8144">
              <div class="fam-titre" style="color:#186A81">${esc(fac)}</div>
              ${src ? `<img src="${src}" style="width:100%;height:auto;display:block">` : ""}
            </div>`
        )
        .join("");

      const nbRisquesTotal = stats?.total ?? 0;

      const imgDoughnutHtml = imgDoughnut
        ? `<div class="chart-box" style="max-width:300px"><div class="chart-title">Répartition par criticité résiduelle</div><img src="${imgDoughnut}"></div>`
        : "";
      const imgDomaineHtml = imgDomaine
        ? `<div class="chart-box"><div class="chart-title">Risques résiduels par domaine</div><img src="${imgDomaine}"></div>`
        : "";
      const familleSectionHtml =
        imgsFamille.length > 0
          ? `<h2>Risques résiduels par famille</h2><div class="fam-grid">${familleSections}</div>`
          : "";
      const facteurSectionHtml =
        imgsFacteur.length > 0
          ? `<h2 style="background:linear-gradient(90deg,#186A81,#0A3475)">Analyse LBC/FT - par facteur de risque</h2><div class="fam-grid">${facteurSections}</div>`
          : "";
      const noDataP = '<p style="color:#999;font-style:italic;padding:8px 0">Aucune donnée.</p>';
      const domaineTable = domaineBody
        ? `<table><thead><tr><th>Domaine</th>${nCols}<th style="text-align:center">Total</th></tr></thead><tbody>${domaineBody}</tbody></table>`
        : noDataP;
      const familleTable = familleBody
        ? `<table><thead><tr><th>Famille</th>${nCols}<th style="text-align:center">Total</th></tr></thead><tbody>${familleBody}</tbody></table>`
        : noDataP;
      const facteurTable = facteurBody
        ? `<h2 style="background:linear-gradient(90deg,#186A81,#0A3475)">Données - par facteur de risque (LBC/FT)</h2><table><thead><tr><th>Facteur de risque</th>${nCols}<th style="text-align:center">Total</th></tr></thead><tbody>${facteurBody}</tbody></table>`
        : "";

      const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Tableau de bord - ${esc(cartoTitre)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:28px;font-weight:700;line-height:1;color:#a5d6a7}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8;flex-wrap:wrap}
  .kpi{flex:1;min-width:80px;padding:14px 12px;border-right:1px solid #f0f0f0;text-align:center;
    border-top:3px solid #0A3475}
  .kpi:last-child{border-right:none}
  .kpi b{display:block;font-size:22px;color:#0A3475;font-weight:700;margin-bottom:2px}
  .kpi span{font-size:10px;color:#666;text-transform:uppercase;letter-spacing:.5px}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}
  .charts-row{display:flex;gap:20px;align-items:flex-start;margin-bottom:20px;page-break-inside:avoid}
  .chart-box{flex:1;border:1px solid #eef0f5;border-radius:8px;padding:14px;background:#fafbff}
  .chart-box img{width:100%;height:auto;display:block}
  .chart-title{font-size:12px;font-weight:700;color:#0A3475;margin-bottom:8px;
    padding-bottom:6px;border-bottom:2px solid #e8edf5}
  .fam-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:20px}
  .fam-card{border:1px solid #eef0f5;border-radius:8px;padding:12px;background:#fafbff;
    page-break-inside:avoid}
  .fam-titre{font-size:11px;font-weight:700;color:#0A3475;border-bottom:1px solid #eef0f5;
    padding-bottom:6px;margin-bottom:8px}
  .fac-card{border:1px solid #186A8130;border-radius:8px;padding:12px;background:#f8fcff;
    border-left:3px solid #186A81;page-break-inside:avoid}
  .fac-titre{font-size:11px;font-weight:700;color:#186A81;border-bottom:1px solid #186A8120;
    padding-bottom:6px;margin-bottom:8px}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1a237e;color:#fff;padding:7px 10px;text-align:left;
    font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:6px 10px;border-bottom:1px solid #eef0f5}
  tr:hover td{background:#f8faff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,th,.hdr-badge,.kpi{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
    .fam-grid{grid-template-columns:repeat(2,1fr)}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eye">ConformIMF · Tableau de bord</div>
  <h1>Tableau de bord Conformité</h1>
  <div class="hdr-sub">Cartographie : ${esc(cartoTitre)}</div>
  <div class="hdr-date">Généré le ${esc(now)}</div>
  <div class="hdr-badge">
    <div class="big">${nbRisquesTotal}</div>
    <div class="small">risques<br>résiduels</div>
  </div>
</div>

<div class="kpi-strip">${kpiHtml}</div>

<div class="content">
  <h2>Visualisations</h2>
  <div class="charts-row">
    ${imgDoughnutHtml}
    ${imgDomaineHtml}
  </div>

  ${familleSectionHtml}

  ${facteurSectionHtml}

  <h2>Données - par domaine</h2>
  ${domaineTable}

  ${avecFacteursDash ? "" : `<h2>Données - par famille</h2>${familleTable}`}

  ${facteurTable}
</div>

<div class="ftr">
  <span>Document confidentiel - ConformIMF</span>
  <strong>${esc(now)}</strong>
</div>
</body>
</html>`;

      const win = window.open("", "_blank", "width=1200,height=900");
      if (!win) {
        showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning");
        return;
      }
      win.document.write(html);
      win.document.close();
    }

    return (
      <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
        {/* Filtres */}
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3} alignItems="center">
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              displayEmpty
              value={selectedCarto}
              onChange={(e) => setSelectedCarto(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  cartographies.find((c) => c.id === v)?.titre
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                    Toutes les cartographies
                  </span>
                )
              }
            >
              <MenuItem value="">Toutes</MenuItem>
              {cartographies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.titre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={selectedDomaine}
              onChange={(e) => setSelectedDomaine(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  domaines.find((d) => d.id === v)?.nom
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
                )
              }
            >
              <MenuItem value="">Tous les domaines</MenuItem>
              {domaines.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Icon>refresh</Icon>}
            onClick={loadStats}
            sx={{ height: "45px" }}
          >
            Actualiser
          </MDButton>
          {stats && (
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<Icon>print</Icon>}
              onClick={handlePrint}
              sx={{ height: "45px" }}
            >
              Imprimer / PDF
            </MDButton>
          )}
        </MDBox>

        {loadingStats ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={36} />
          </MDBox>
        ) : !stats ? (
          <MDBox textAlign="center" py={6}>
            <Icon sx={{ fontSize: 48, color: "#ccc" }}>bar_chart</Icon>
            <MDTypography variant="body2" color="text" mt={1}>
              Cliquez sur Actualiser pour charger les statistiques.
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox>
            {/* KPI : Total + un card par niveau de criticité + écarts */}
            {(() => {
              const niveaux = stats.niveaux || [];
              const parNiveau = stats.par_niveau || {};
              return (
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={4} lg={2} key="total">
                    <Card
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "#e8edf5",
                        border: "1px solid #0A347522",
                        borderRadius: "12px",
                      }}
                    >
                      <Icon sx={{ fontSize: 32, color: "#0A3475", mb: 0.5 }}>warning_amber</Icon>
                      <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#0A3475" }}>
                        {stats.total}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total risques résiduels
                      </MDTypography>
                    </Card>
                  </Grid>
                  {niveaux.map((n) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={n.nom}>
                      <Card
                        sx={{
                          p: 2,
                          textAlign: "center",
                          bgcolor: n.couleur + "22",
                          border: `1px solid ${n.couleur}44`,
                          borderRadius: "12px",
                        }}
                      >
                        <Icon sx={{ fontSize: 32, color: n.couleur, mb: 0.5 }}>
                          {n.nom === niveaux[niveaux.length - 1]?.nom
                            ? "priority_high"
                            : n.nom === niveaux[0]?.nom
                            ? "check"
                            : "remove"}
                        </Icon>
                        <MDTypography variant="h3" fontWeight="bold" sx={{ color: n.couleur }}>
                          {parNiveau[n.nom] ?? 0}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {n.nom}
                        </MDTypography>
                      </Card>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4} lg={2} key="ecarts">
                    <Card
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "#f3e5f5",
                        border: "1px solid #7b1fa222",
                        borderRadius: "12px",
                      }}
                    >
                      <Icon sx={{ fontSize: 32, color: "#7b1fa2", mb: 0.5 }}>report_problem</Icon>
                      <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#7b1fa2" }}>
                        {stats.nb_ecarts_procedures}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Écarts de procédures
                      </MDTypography>
                    </Card>
                  </Grid>
                </Grid>
              );
            })()}

            {/* Graphiques */}
            {(() => {
              const niveaux = stats.niveaux || [];
              const nbFacteurs = Object.keys(stats.par_facteur || {}).length;
              const facteurLabel = `${nbFacteurs} facteur${nbFacteurs > 1 ? "s" : ""}`;
              const mkDatasets = (byKey) =>
                niveaux.map((n) => ({
                  label: n.nom,
                  data: Object.values(byKey).map((d) => d[n.nom] || 0),
                  backgroundColor: n.couleur,
                }));

              return (
                <Grid container spacing={3}>
                  {/* Doughnut : répartition par niveau de criticité résiduel */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                        Répartition par criticité résiduelle
                      </MDTypography>
                      {stats.total === 0 ? (
                        <MDBox textAlign="center" py={4}>
                          <MDTypography variant="caption" color="text">
                            Aucun risque enregistré.
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <Doughnut
                          ref={doughnutRef}
                          data={{
                            labels: niveaux.map((n) => n.nom),
                            datasets: [
                              {
                                data: niveaux.map((n) => stats.par_niveau?.[n.nom] ?? 0),
                                backgroundColor: niveaux.map((n) => n.couleur),
                                borderWidth: 2,
                                borderColor: "#fff",
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            plugins: {
                              legend: { position: "bottom" },
                              tooltip: {
                                callbacks: {
                                  label: (ctx) =>
                                    ` ${ctx.label} : ${ctx.parsed} risque${
                                      ctx.parsed > 1 ? "s" : ""
                                    }`,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </Card>
                  </Grid>

                  {/* Bar : risques résiduels par domaine */}
                  <Grid item xs={12} md={8}>
                    <Card sx={{ p: 2 }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                        Risques résiduels par domaine
                      </MDTypography>
                      {Object.keys(stats.par_domaine).length === 0 ? (
                        <MDBox textAlign="center" py={4}>
                          <MDTypography variant="caption" color="text">
                            Aucune donnée disponible.
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <Bar
                          ref={barDomaineRef}
                          data={{
                            labels: Object.keys(stats.par_domaine),
                            datasets: mkDatasets(stats.par_domaine),
                          }}
                          options={{
                            responsive: true,
                            plugins: { legend: { position: "top" } },
                            scales: {
                              x: { stacked: true },
                              y: { stacked: true, ticks: { stepSize: 1 } },
                            },
                          }}
                        />
                      )}
                    </Card>
                  </Grid>

                  {/* Un diagramme par famille de risque - masqué pour une
                      cartographie en mode facteurs (LBC/FT) */}
                  {!avecFacteursDash &&
                    (Object.keys(stats.par_famille || {}).length === 0 ? (
                      <Grid item xs={12}>
                        <Card sx={{ p: 2 }}>
                          <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                            Risques résiduels par famille
                          </MDTypography>
                          <MDBox textAlign="center" py={4}>
                            <MDTypography variant="caption" color="text">
                              Aucune famille de risque renseignée.
                            </MDTypography>
                          </MDBox>
                        </Card>
                      </Grid>
                    ) : (
                      Object.entries(stats.par_famille).map(([famille, data]) => (
                        <Grid item xs={12} sm={6} md={4} key={famille}>
                          <Card sx={{ p: 2, height: "100%" }}>
                            <MDTypography
                              variant="subtitle2"
                              fontWeight="bold"
                              mb={1}
                              sx={{
                                color: "#0A3475",
                                borderBottom: "2px solid #e0e8f8",
                                pb: 0.5,
                              }}
                            >
                              {famille}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ opacity: 0.65, display: "block", mb: 1 }}
                            >
                              {data.total} risque{data.total > 1 ? "s" : ""} résiduel
                              {data.total > 1 ? "s" : ""}
                            </MDTypography>
                            <Bar
                              ref={(el) => {
                                if (el) familleChartRefs.current[famille] = el;
                                else delete familleChartRefs.current[famille];
                              }}
                              data={{
                                labels: niveaux.map((n) => n.nom),
                                datasets: [
                                  {
                                    label: "Risques résiduels",
                                    data: niveaux.map((n) => data[n.nom] || 0),
                                    backgroundColor: niveaux.map((n) => n.couleur),
                                    borderRadius: 4,
                                    borderSkipped: false,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false },
                                  tooltip: {
                                    callbacks: {
                                      label: (ctx) =>
                                        ` ${ctx.parsed.y} risque${ctx.parsed.y > 1 ? "s" : ""}`,
                                    },
                                  },
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: { stepSize: 1, precision: 0 },
                                    title: { display: true, text: "Nb risques" },
                                  },
                                  x: {
                                    grid: { display: false },
                                  },
                                },
                              }}
                            />
                          </Card>
                        </Grid>
                      ))
                    ))}

                  {/* Un diagramme par facteur de risque LBC/FT - uniquement si avec_facteurs */}
                  {showFacteurSection && nbFacteurs > 0 && (
                    <>
                      {/* ── Séparateur de section LBC/FT ── */}
                      <Grid item xs={12}>
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mt: 1,
                            py: 1.2,
                            px: 2,
                            borderRadius: 2,
                            background: "linear-gradient(90deg,#186A8112 0%,transparent 100%)",
                            borderLeft: "4px solid #186A81",
                          }}
                        >
                          <Icon sx={{ color: "#186A81", fontSize: "20px !important" }}>
                            security
                          </Icon>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ color: "#186A81", flex: 1 }}
                          >
                            Analyse LBC/FT - Risques résiduels par facteur de risque
                          </MDTypography>
                          <MDBox
                            sx={{
                              background: "#186A8118",
                              border: "1px solid #186A8135",
                              borderRadius: "12px",
                              px: 1.5,
                              py: 0.3,
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{ color: "#186A81", fontWeight: 700 }}
                            >
                              {facteurLabel}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>

                      {/* ── Cartes par facteur ── */}
                      {Object.entries(stats.par_facteur).map(([facteur, data]) => {
                        const totalFac = data.total || 0;
                        const totalPl = totalFac > 1 ? "s" : "";
                        return (
                          <Grid item xs={12} sm={6} md={4} key={facteur}>
                            <Card
                              sx={{
                                height: "100%",
                                borderLeft: "4px solid #186A81",
                                boxShadow: "0 2px 10px rgba(24,106,129,0.10)",
                                overflow: "hidden",
                              }}
                            >
                              {/* Bandeau titre */}
                              <MDBox
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  px: 2,
                                  pt: 1.5,
                                  pb: 1,
                                  background: "linear-gradient(135deg,#186A810d 0%,#0A34750a 100%)",
                                  borderBottom: "1px solid #186A8118",
                                }}
                              >
                                <MDTypography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ color: "#186A81", fontSize: "12px" }}
                                >
                                  {facteur}
                                </MDTypography>
                                <MDBox
                                  sx={{
                                    background: "#186A8115",
                                    border: "1px solid #186A8130",
                                    borderRadius: "10px",
                                    px: 1.2,
                                    py: 0.2,
                                    ml: 1,
                                    flexShrink: 0,
                                  }}
                                >
                                  <MDTypography
                                    variant="caption"
                                    sx={{ color: "#186A81", fontWeight: 700, fontSize: "11px" }}
                                  >
                                    {totalFac} risque{totalPl}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>

                              {/* Graphique */}
                              <MDBox sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
                                <Bar
                                  ref={(el) => {
                                    if (el) facteurChartRefs.current[facteur] = el;
                                    else delete facteurChartRefs.current[facteur];
                                  }}
                                  data={{
                                    labels: niveaux.map((n) => n.nom),
                                    datasets: [
                                      {
                                        label: "Risques résiduels",
                                        data: niveaux.map((n) => data[n.nom] || 0),
                                        backgroundColor: niveaux.map((n) => n.couleur),
                                        borderRadius: 6,
                                        borderSkipped: false,
                                      },
                                    ],
                                  }}
                                  options={{
                                    responsive: true,
                                    plugins: {
                                      legend: { display: false },
                                      tooltip: {
                                        backgroundColor: "#0a3475ee",
                                        padding: 10,
                                        callbacks: {
                                          label: (ctx) => {
                                            const v = ctx.parsed.y;
                                            const pl = v > 1 ? "s" : "";
                                            return ` ${v} risque${pl}`;
                                          },
                                          afterLabel: (ctx) => {
                                            const v = ctx.parsed.y;
                                            const pct =
                                              totalFac > 0 ? Math.round((v / totalFac) * 100) : 0;
                                            return ` soit ${pct}% du facteur`;
                                          },
                                        },
                                      },
                                    },
                                    scales: {
                                      y: {
                                        beginAtZero: true,
                                        ticks: {
                                          stepSize: 1,
                                          precision: 0,
                                          font: { size: 10 },
                                        },
                                        grid: { color: "#f0f4f8" },
                                      },
                                      x: {
                                        grid: { display: false },
                                        ticks: { font: { size: 10 } },
                                      },
                                    },
                                  }}
                                />
                              </MDBox>
                            </Card>
                          </Grid>
                        );
                      })}
                    </>
                  )}
                </Grid>
              );
            })()}
          </MDBox>
        )}
      </MDBox>
    );
  }

  // ── Dialog création / modification procédure ────────────────────────────

  function renderDialogProc() {
    return (
      <Dialog open={dialogProc} onClose={() => setDialogProc(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingProc ? "Modifier la procédure" : "Nouvelle procédure"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Titre *"
              fullWidth
              size="small"
              value={formProc.titre}
              onChange={(e) => setFormProc((p) => ({ ...p, titre: e.target.value }))}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Résumé"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formProc.resume}
              onChange={(e) => setFormProc((p) => ({ ...p, resume: e.target.value }))}
              inputProps={{ maxLength: 1000 }}
              helperText={`${formProc.resume.length}/1000`}
            />
            <TextField
              label="Date d'adoption"
              type="date"
              fullWidth
              size="small"
              value={formProc.date_adoption}
              onChange={(e) => setFormProc((p) => ({ ...p, date_adoption: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Domaine</InputLabel>
              <Select
                value={formProc.domaine_id}
                label="Domaine"
                onChange={(e) => setFormProc((p) => ({ ...p, domaine_id: e.target.value }))}
              >
                <MenuItem value="">Aucun</MenuItem>
                {domaines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MDBox>
              <MDTypography variant="caption" color="text" mb={0.5} display="block">
                {editingProc
                  ? "Nouveau PDF (optionnel - crée une nouvelle version)"
                  : "Fichier PDF *"}
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                component="label"
                startIcon={<Icon>upload_file</Icon>}
              >
                {fileProc ? fileProc.name : "Choisir un fichier PDF"}
                <input
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => setFileProc(e.target.files[0] || null)}
                />
              </MDButton>
            </MDBox>
            {saving && uploadProgress > 0 && (
              <MDBox>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <MDTypography variant="caption">{uploadProgress}%</MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogProc(false)}
            disabled={saving}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveProc}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Dialog ligne de référentiel ─────────────────────────────────────────

  function renderDialogRef() {
    const selectedExigence = exigences.find((e) => e.id === Number(formRef.exigence_id)) || null;
    const activeProcedures = procedures.filter((p) => p.statut === "ACTIF");
    return (
      <Dialog open={dialogRef} onClose={() => setDialogRef(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingRef ? "Modifier la ligne de référentiel" : "Ajouter une ligne au référentiel"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Code"
              fullWidth
              size="small"
              value={formRef.code}
              InputProps={{ readOnly: !!editingRef }}
              onChange={(e) => !editingRef && setFormRef((f) => ({ ...f, code: e.target.value }))}
              inputProps={{ maxLength: 20 }}
              helperText={editingRef ? "Non modifiable" : "Ex : LBC01 - optionnel"}
              sx={editingRef ? { "& input": { color: "#9e9e9e" } } : {}}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Domaine</InputLabel>
              <Select
                value={dialogDomaineRef}
                label="Domaine"
                onChange={(e) => {
                  const val = e.target.value;
                  setDialogDomaineRef(val);
                  setFormRef((f) => ({ ...f, exigence_id: "" }));
                  loadExigences(val);
                }}
              >
                <MenuItem value="">Tous les domaines</MenuItem>
                {domaines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              options={exigences}
              getOptionLabel={(e) =>
                `${e.texte_titre ? `${e.texte_titre} - ` : ""}${
                  e.reference ? `[${e.reference}] ` : ""
                }${e.exigence.slice(0, 80)}${e.exigence.length > 80 ? "…" : ""}`
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <MDBox display="flex" flexDirection="column" py={0.5}>
                    {option.texte_titre && (
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                        sx={{ opacity: 0.65, lineHeight: 1.3, mb: 0.25 }}
                      >
                        {option.texte_titre}
                      </MDTypography>
                    )}
                    <MDTypography variant="button" fontWeight="regular" sx={{ lineHeight: 1.4 }}>
                      {option.reference ? `[${option.reference}] ` : ""}
                      {option.exigence.slice(0, 120)}
                      {option.exigence.length > 120 ? "…" : ""}
                    </MDTypography>
                  </MDBox>
                </li>
              )}
              value={selectedExigence}
              onChange={(_, val) =>
                setFormRef((f) => ({
                  ...f,
                  exigence_id: val ? val.id : "",
                }))
              }
              loading={loadingExigences}
              disabled={!!editingRef}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Exigence *"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingExigences && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="Aucune exigence trouvée"
            />
            {selectedExigence && (
              <MDBox
                sx={{
                  borderLeft: "3px solid",
                  borderColor: "info.main",
                  bgcolor: "rgba(26, 115, 232, 0.06)",
                  borderRadius: "0 6px 6px 0",
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {selectedExigence.texte_titre && (
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: "14px !important", color: "info.main", mt: "1px" }}>
                      gavel
                    </Icon>
                    <MDTypography variant="caption" color="info" fontWeight="medium">
                      {selectedExigence.texte_titre}
                    </MDTypography>
                  </MDBox>
                )}
                {selectedExigence.domaine_nom && (
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: "14px !important", color: "text.secondary", mt: "1px" }}>
                      category
                    </Icon>
                    <MDTypography variant="caption" color="text">
                      {selectedExigence.domaine_nom}
                    </MDTypography>
                  </MDBox>
                )}
                {selectedExigence.reference && (
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: "14px !important", color: "text.secondary", mt: "1px" }}>
                      tag
                    </Icon>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      {selectedExigence.reference}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox display="flex" gap={0.75} pt={0.25}>
                  <Icon
                    sx={{
                      fontSize: "14px !important",
                      color: "text.secondary",
                      mt: "1px",
                      flexShrink: 0,
                    }}
                  >
                    description
                  </Icon>
                  <MDTypography variant="caption" color="text" sx={{ lineHeight: 1.5 }}>
                    {selectedExigence.exigence}
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Procédure interne</InputLabel>
              <Select
                value={formRef.procedure_id}
                label="Procédure interne"
                onChange={(e) => {
                  const val = e.target.value;
                  setFormRef((f) => ({
                    ...f,
                    procedure_id: val,
                    ecart_procedure: val ? f.ecart_procedure : false,
                  }));
                }}
              >
                <MenuItem value="">Aucune</MenuItem>
                {activeProcedures.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.titre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formRef.procedure_id && (
              <MDBox
                onClick={() => setFormRef((f) => ({ ...f, ecart_procedure: !f.ecart_procedure }))}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: formRef.ecart_procedure ? "#e65100" : "#2e7d32",
                  bgcolor: formRef.ecart_procedure ? "#fff8e1" : "#e8f5e9",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  checked={formRef.ecart_procedure}
                  onChange={(e) => {
                    e.stopPropagation();
                    setFormRef((f) => ({ ...f, ecart_procedure: e.target.checked }));
                  }}
                  color={formRef.ecart_procedure ? "warning" : "success"}
                  size="small"
                  sx={{ p: 0 }}
                />
                <MDBox>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      color: formRef.ecart_procedure ? "#e65100" : "#2e7d32",
                      display: "block",
                    }}
                  >
                    Signaler un écart de procédure
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#888", fontSize: "0.68rem" }}>
                    La procédure ne couvre pas complètement cette exigence
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
            <TextField
              label="Observations"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formRef.observations}
              onChange={(e) => setFormRef((f) => ({ ...f, observations: e.target.value }))}
              inputProps={{ maxLength: 2000 }}
              helperText={`${formRef.observations.length}/2000`}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogRef(false)}
            disabled={savingRef}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveRef}
            disabled={savingRef}
          >
            {savingRef ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Dialog risque de conformité ─────────────────────────────────────────

  function renderDialogRisque() {
    return (
      <Dialog open={dialogRisque} onClose={() => setDialogRisque(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, background: "linear-gradient(195deg, #0A3475, #186A81)" }}>
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#FFD740" }}>
            Évaluation des contrôles
          </MDTypography>
          {editingRisque && (
            <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
              {editingRisque.code ? `[${editingRisque.code}] ` : ""}
              {editingRisque.titre}
            </MDTypography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2.5} pt={1}>
            {/* Exigence liée */}
            <FormControl fullWidth size="small">
              <InputLabel>Exigence liée</InputLabel>
              <Select
                value={formRisque.exigence_id}
                label="Exigence liée"
                onChange={(e) => setFormRisque((f) => ({ ...f, exigence_id: e.target.value }))}
              >
                <MenuItem value="">Aucune</MenuItem>
                {exigences.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.reference ? `[${e.reference}] ` : ""}
                    {e.exigence?.slice(0, 60)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* Contrôles QC / EO */}
            <MDTypography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ color: "#0A3475", borderBottom: "2px solid #e0e8f8", pb: 0.5 }}
            >
              Dispositif de contrôle
            </MDTypography>
            <Grid container spacing={3}>
              {(() => {
                const sorted = [...niveauxCriticite].sort((a, b) => a.ordre - b.ordre);
                const nc = sorted.length ? sorted.map((n) => n.couleur) : undefined;
                const nLen = configRisque.nb_niveaux_dmr || 5;
                const ctrlColors = nc
                  ? Array.from(
                      { length: nLen },
                      (_, i) => nc[Math.round((i / Math.max(nLen - 1, 1)) * (nc.length - 1))]
                    )
                  : undefined;
                return (
                  <>
                    <Grid item xs={12} sm={6}>
                      <ScoreButtons
                        label="Qualité des contrôles"
                        value={formRisque.qualite_controles}
                        labels={configRisque.labels_adequation || []}
                        colors={ctrlColors}
                        onChange={(v) => setFormRisque((f) => ({ ...f, qualite_controles: v }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ScoreButtons
                        label="Efficacité opérationnelle"
                        value={formRisque.efficacite_operationnelle}
                        labels={configRisque.labels_efficacite || []}
                        colors={ctrlColors}
                        onChange={(v) =>
                          setFormRisque((f) => ({ ...f, efficacite_operationnelle: v }))
                        }
                      />
                    </Grid>
                  </>
                );
              })()}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogRisque(false)}
            disabled={savingRisque}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveRisque}
            disabled={savingRisque}
          >
            {savingRisque ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Dialog plan d'action ────────────────────────────────────────────────

  function renderDialogPlan() {
    return (
      <Dialog open={dialogPlan} onClose={() => setDialogPlan(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingPlan ? "Modifier le plan d'action" : "Nouveau plan d'action"}
          </MDTypography>
          {planRisque && (
            <MDTypography variant="caption" color="text">
              Risque : {planRisque.titre}
            </MDTypography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Action corrective *"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formPlan.action}
              onChange={(e) => setFormPlan((f) => ({ ...f, action: e.target.value }))}
              inputProps={{ maxLength: 1000 }}
              helperText={`${formPlan.action.length}/1000`}
            />
            <TextField
              label="Responsable"
              fullWidth
              size="small"
              value={formPlan.responsable}
              onChange={(e) => setFormPlan((f) => ({ ...f, responsable: e.target.value }))}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Échéance"
              type="date"
              fullWidth
              size="small"
              value={formPlan.echeance}
              onChange={(e) => setFormPlan((f) => ({ ...f, echeance: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={formPlan.statut}
                label="Statut"
                onChange={(e) => setFormPlan((f) => ({ ...f, statut: e.target.value }))}
              >
                <MenuItem value="EN_COURS">En cours</MenuItem>
                <MenuItem value="REALISE">Réalisé</MenuItem>
                <MenuItem value="REPORTE">Reporté</MenuItem>
              </Select>
            </FormControl>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogPlan(false)}
            disabled={savingPlan}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={savePlan}
            disabled={savingPlan}
          >
            {savingPlan ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Dialog versions ─────────────────────────────────────────────────────

  function renderDialogVersions() {
    if (!versionsProc) return null;
    const versions = [...(versionsProc.versions || [])].sort(
      (a, b) => new Date(b.date_version) - new Date(a.date_version)
    );
    return (
      <Dialog
        open={dialogVersions}
        onClose={() => setDialogVersions(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold">
            Versions - {versionsProc.titre}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          {versions.length === 0 ? (
            <MDTypography variant="body2" color="text">
              Aucune version disponible.
            </MDTypography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  {["Version", "Date", "Fichier", "Statut", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => {
                  const doc = v.documents?.[v.documents.length - 1];
                  return (
                    <TableRow key={v.id}>
                      <TableCell>
                        <Chip
                          label={`v${v.version}`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="caption">
                          {new Date(v.date_version).toLocaleDateString("fr-FR")}
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="caption">{doc?.nom_fichier || "-"}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={v.actif ? "Actif" : "Inactif"}
                          size="small"
                          sx={{
                            bgcolor: v.actif ? "#e8f5e9" : "#f5f5f5",
                            color: v.actif ? "#2e7d32" : "#757575",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <MDBox display="flex" gap={0.5}>
                          {doc && (
                            <Tooltip title="Voir le PDF">
                              <IconButton
                                size="small"
                                onClick={() => openViewer(versionsProc, v.id)}
                                sx={{ color: "#186A81" }}
                              >
                                <Icon sx={{ fontSize: "1rem" }}>visibility</Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasWrite && (
                            <Tooltip title={v.actif ? "Désactiver" : "Activer"}>
                              <IconButton
                                size="small"
                                onClick={() => toggleVersion(versionsProc.id, v.id)}
                                sx={{ color: v.actif ? "#d32f2f" : "#2e7d32" }}
                              >
                                <Icon sx={{ fontSize: "1rem" }}>
                                  {v.actif ? "toggle_off" : "toggle_on"}
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                        </MDBox>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogVersions(false)}
          >
            Fermer
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Dialog viewer PDF ───────────────────────────────────────────────────

  function renderDialogViewer() {
    function closeViewer() {
      setDialogViewer(false);
      if (viewerUrl) {
        URL.revokeObjectURL(viewerUrl);
        setViewerUrl("");
      }
    }
    return (
      <Dialog open={dialogViewer} onClose={closeViewer} maxWidth="lg" fullWidth>
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold">
              {viewerTitre}
            </MDTypography>
            <IconButton size="small" onClick={closeViewer}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "75vh" }}>
          <iframe
            src={viewerUrl}
            title={viewerTitre}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // ── Rendu Reporting réglementaire (Tab F) ───────────────────────────────

  function renderReporting() {
    const avecFacteursReport =
      cartographies.find((c) => c.id === Number(selectedCartoReport))?.avec_facteurs === true;
    const grouped = groupRisques(reportRisques, avecFacteursReport);
    const totalPlansRapport = reportRisques.reduce(
      (acc, r) => acc + (r.plans_action?.length || 0),
      0
    );

    return (
      <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
        {/* Sélecteurs et boutons */}
        <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={3}>
          <FormControl sx={{ minWidth: 220 }}>
            <Select
              displayEmpty
              value={selectedCartoReport}
              onChange={(e) => {
                setSelectedCartoReport(e.target.value);
                setReportRisques([]);
                setReportStats(null);
              }}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  cartographies.find((c) => c.id === v)?.titre
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                    Sélectionner une cartographie
                  </span>
                )
              }
            >
              <MenuItem value="">- Sélectionner -</MenuItem>
              {cartographies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.titre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={selectedDomaineReport}
              onChange={(e) => setSelectedDomaineReport(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  domaines.find((d) => d.id === v)?.nom
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
                )
              }
            >
              <MenuItem value="">Tous les domaines</MenuItem>
              {domaines.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={reportLoading ? <CircularProgress size={14} /> : <Icon>cloud_download</Icon>}
            onClick={loadReportData}
            disabled={reportLoading || !selectedCartoReport}
            sx={{ height: "45px" }}
          >
            Charger les données
          </MDButton>

          {reportRisques.length > 0 && (
            <MDButton
              variant="gradient"
              sx={{
                background: "linear-gradient(195deg, #0A3475, #186A81)",
                color: "#fff",
                height: "45px",
              }}
              size="small"
              startIcon={<Icon>print</Icon>}
              onClick={generateReport}
            >
              Générer &amp; Imprimer
            </MDButton>
          )}
        </MDBox>

        {/* Aperçu rapide */}
        {reportRisques.length > 0 && reportStats && (
          <MDBox display="flex" gap={2} mb={3} flexWrap="wrap">
            {[
              { label: "Risques", value: reportStats.total, color: "#0A3475" },
              ...(reportStats.niveaux || [])
                .slice()
                .sort((a, b) => b.score_min - a.score_min)
                .map((n) => ({
                  label: n.nom,
                  value: reportStats.par_niveau?.[n.nom] ?? 0,
                  color: n.couleur,
                })),
              { label: "Plans d'action", value: totalPlansRapport, color: "#1565c0" },
              { label: "Domaines", value: grouped.length, color: "#6a1b9a" },
            ].map(({ label, value, color }) => (
              <MDBox
                key={label}
                sx={{
                  border: `1px solid ${color}44`,
                  bgcolor: `${color}11`,
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.75,
                  minWidth: 80,
                  textAlign: "center",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" sx={{ color }}>
                  {value}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {label}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        )}

        {/* Aperçu domaines */}
        {reportRisques.length > 0 && (
          <MDBox>
            <MDTypography variant="subtitle2" fontWeight="bold" mb={1.5} sx={{ color: "#0A3475" }}>
              Aperçu par domaine
            </MDTypography>
            <MDBox display="flex" gap={2} flexWrap="wrap">
              {grouped.map(({ domaine, categories }) => {
                const domaineRisques = categories.flatMap((c) => c.list);
                const niveauCounts = Object.fromEntries(
                  niveauxCriticite.map((n) => [
                    n.nom,
                    domaineRisques.filter((r) => r.niveau_criticite === n.nom).length,
                  ])
                );
                return (
                  <MDBox
                    key={domaine}
                    sx={{
                      border: "1px solid #dce3ef",
                      borderRadius: 2,
                      p: 1.5,
                      minWidth: 200,
                      flex: "1 1 200px",
                      maxWidth: 320,
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: "#0A3475", display: "block", mb: 0.75 }}
                    >
                      {domaine}
                    </MDTypography>
                    <MDBox display="flex" gap={0.75} flexWrap="wrap">
                      <Chip
                        label={`${domaineRisques.length} risque${
                          domaineRisques.length > 1 ? "s" : ""
                        }`}
                        size="small"
                        sx={{
                          bgcolor: "#e8edf5",
                          color: "#0A3475",
                          fontSize: "0.65rem",
                          height: 18,
                        }}
                      />
                      {niveauxCriticite
                        .slice()
                        .sort((a, b) => b.score_min - a.score_min)
                        .map((n) =>
                          niveauCounts[n.nom] > 0 ? (
                            <Chip
                              key={n.nom}
                              label={`${niveauCounts[n.nom]} ${n.nom}`}
                              size="small"
                              sx={{
                                bgcolor: n.couleur + "22",
                                color: n.couleur,
                                fontSize: "0.65rem",
                                height: 18,
                              }}
                            />
                          ) : null
                        )}
                    </MDBox>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                      {categories.length} catégorie{categories.length > 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>
                );
              })}
            </MDBox>
          </MDBox>
        )}

        {reportRisques.length === 0 && !reportLoading && (
          <MDBox textAlign="center" py={8}>
            <Icon sx={{ fontSize: 56, color: "#ccc" }}>print</Icon>
            <MDTypography variant="h6" color="text" mt={1}>
              Aucune donnée chargée
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Sélectionnez une cartographie et cliquez sur &quot;Charger les données&quot;.
            </MDTypography>
          </MDBox>
        )}

        {reportLoading && (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={36} />
          </MDBox>
        )}
      </MDBox>
    );
  }

  // ── Placeholder ─────────────────────────────────────────────────────────

  function renderComingSoon(label, icon, semaine) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
        sx={{ animation: `${fadeInUp} 0.4s ease both` }}
      >
        <Icon sx={{ fontSize: 56, color: "#ccc", mb: 2 }}>{icon}</Icon>
        <MDTypography variant="h6" color="text" fontWeight="medium">
          {label}
        </MDTypography>
        <MDTypography variant="body2" color="text" mt={0.5}>
          {semaine
            ? `Disponible en ${semaine}.`
            : "Ce module sera disponible dans les prochaines semaines."}
        </MDTypography>
      </MDBox>
    );
  }

  function renderDialogPlansView() {
    if (!dialogPlansView) return null;
    // Toujours lire les données fraîches depuis risques (mis à jour après save/delete)
    const liveRisque = risques.find((r) => r.id === dialogPlansView.id) || dialogPlansView;
    return (
      <Dialog
        open={!!dialogPlansView}
        onClose={() => setDialogPlansView(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, background: "linear-gradient(195deg, #0A3475, #186A81)" }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#FFD740" }}>
                Plans d&apos;action
              </MDTypography>
              {liveRisque.code && (
                <Chip
                  label={liveRisque.code}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontSize: "0.65rem",
                    height: 18,
                    mt: 0.5,
                  }}
                />
              )}
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.85)", display: "block", mt: 0.25 }}
              >
                {liveRisque.titre}
              </MDTypography>
            </MDBox>
            <IconButton
              onClick={() => setDialogPlansView(null)}
              sx={{ color: "#FFD740", mt: -0.5 }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>{renderPlansInline(liveRisque)}</DialogContent>
      </Dialog>
    );
  }

  // ── Rendu par onglet ────────────────────────────────────────────────────

  function renderTabContent() {
    switch (activeTab) {
      case 0:
        return (
          <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
            {renderFilters()}
            {renderProceduresTable()}
          </MDBox>
        );
      case 1:
        return (
          <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
            {renderFiltersRef()}
            {renderReferentielTable()}
          </MDBox>
        );
      case 2:
        return (
          <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
            {renderFiltersCartographie()}
            {renderRisqueRegistre()}
          </MDBox>
        );
      case 3:
        return renderPlansTab();
      case 4:
        return renderDashboard();
      case 5:
        return renderReporting();
      default:
        return null;
    }
  }

  // ── Rendu principal ─────────────────────────────────────────────────────
  const showFacteurSection = Object.keys(stats?.par_facteur || {}).length > 0;

  return (
    <MDBox>
      {/* Card avec onglets */}
      <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
        <MDBox px={2} pt={2} pb={2}>
          <MDBox
            display="flex"
            flexWrap="wrap"
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor: "#f8f9fa",
              width: "fit-content",
            }}
          >
            {visibleTabs.map((t, idx) => {
              const i = idx + skipFirstTabs;
              return (
                <div
                  key={i}
                  onClick={() => setActiveTab(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    cursor: "pointer",
                    background:
                      activeTab === i ? "linear-gradient(195deg, #0A3475, #186A81)" : "transparent",
                    color: activeTab === i ? "#FFD740" : "#555555",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== i) e.currentTarget.style.backgroundColor = "#e8edf5";
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== i) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span>{t.label}</span>
                </div>
              );
            })}
          </MDBox>
        </MDBox>
      </Card>

      {/* Contenu de l'onglet actif */}
      <Card sx={{ mt: 3 }}>
        <MDBox p={3}>{renderTabContent()}</MDBox>
      </Card>

      {/* Dialogs */}
      {renderDialogProc()}
      {renderDialogRef()}
      {renderDialogVersions()}
      {renderDialogViewer()}
      {renderDialogRisque()}
      {renderDialogPlan()}
      {renderDialogPlansView()}

      {/* Dialog de confirmation */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

// ── HTML Suivi Plans d'action (Conformité) ────────────────────────────────────

function buildSuiviConformiteHtml(risques, niveaux, config, cartographie) {
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const now = new Date();
  const dateFmt = now.toLocaleString("fr-FR");
  const lbl = (arr, idx) => (arr && idx != null && arr[idx - 1] ? arr[idx - 1] : null);

  const STATUT_LABEL = { EN_COURS: "En cours", REALISE: "Réalisé", REPORTE: "Reporté" };
  const STATUT_COLOR = { EN_COURS: "#1565c0", REALISE: "#2e7d32", REPORTE: "#e65100" };
  const STATUT_BG = { EN_COURS: "#e3f2fd", REALISE: "#e8f5e9", REPORTE: "#fff3e0" };

  const levelFor = (score) => {
    if (!score || !niveaux?.length) return null;
    return niveaux.find((n) => score >= n.score_min && score <= n.score_max) || null;
  };

  const allPlans = [];
  risques.forEach((r) => {
    (r.plans_action || []).forEach((p) => allPlans.push({ ...p, risque: r }));
  });

  const total = allPlans.length;
  const enCours = allPlans.filter((p) => p.statut === "EN_COURS").length;
  const realises = allPlans.filter((p) => p.statut === "REALISE").length;
  const reportes = allPlans.filter((p) => p.statut === "REPORTE").length;
  const enRetard = allPlans.filter(
    (p) => p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < now
  ).length;
  const tauxRealisation = total > 0 ? Math.round((realises / total) * 100) : 0;
  const tauxColor =
    tauxRealisation >= 80 ? "#2e7d32" : tauxRealisation >= 50 ? "#e65100" : "#c62828";
  const hdrBadgeColor =
    tauxColor === "#2e7d32" ? "#a5d6a7" : tauxColor === "#e65100" ? "#ffcc80" : "#ef9a9a";

  const risquesAvecPlans = risques.filter((r) => (r.plans_action || []).length > 0);
  const cartoTitre = esc(cartographie?.titre || "");
  const cartoInst = esc(cartographie?.institution || "ConformIMF");

  // ── Lignes plans par risque ────────────────────────────────────────────────
  const risqueRows = risquesAvecPlans
    .map((r) => {
      const lvRes = levelFor(r.risque_residuel);
      const lvInh = levelFor(r.score_inherent);
      const lv = lvRes || lvInh;
      const levelBg = lv ? `${lv.couleur}18` : "#f5f7fb";
      const rCode = esc(r.code || `R${String(r.id).padStart(3, "0")}`);
      const rTitre = esc(r.titre);
      const impactLbl = lbl(config?.labels_impact, r.impact);
      const probaLbl = lbl(config?.labels_probabilite, r.probabilite);
      const riLabel = impactLbl && probaLbl ? `RI = ${impactLbl} × ${probaLbl}` : "";
      const exigenceRef = esc(r.exigence?.reference || "");
      const nbPlans = (r.plans_action || []).length;
      const plansPl = nbPlans > 1 ? "s" : "";
      const lvColor = lv ? lv.couleur : "";
      const lvNomEsc = lv ? esc(lv.nom) : "";
      const lvBadge = lv
        ? `<span style="background:${lvColor};color:#fff;padding:1px 9px;border-radius:8px;font-size:10px;font-weight:700">${lvNomEsc}</span>`
        : "";
      const riSpan = riLabel
        ? `<span style="color:#888;font-size:10px">${esc(riLabel)}</span>`
        : "";

      const planRows = (r.plans_action || [])
        .map((p, idx) => {
          const ech = p.echeance ? new Date(p.echeance) : null;
          const isLate = ech && p.statut !== "REALISE" && ech < now;
          const soonD = new Date(now);
          soonD.setDate(now.getDate() + 7);
          const isSoon = ech && p.statut !== "REALISE" && !isLate && ech < soonD;
          const echeanceFmt = ech ? ech.toLocaleDateString("fr-FR") : "-";
          const echStyle = isLate
            ? "color:#c62828;font-weight:700"
            : isSoon
            ? "color:#e65100;font-weight:700"
            : "";
          const echSuffix = isLate ? " ⚠" : isSoon ? " 🔔" : "";
          const statutBg = STATUT_BG[p.statut] || "#f8f9fa";
          const statutColor = STATUT_COLOR[p.statut] || "#757575";
          const statutLabel = esc(STATUT_LABEL[p.statut] || p.statut);
          const pAction = esc(p.action);
          const pResp = esc(p.responsable || "-");
          return `<tr>
            <td style="color:#bbb;font-size:10px;text-align:center;width:28px">${idx + 1}</td>
            <td style="font-size:11px;color:#5c6bc0;width:100px">${exigenceRef || "-"}</td>
            <td style="font-size:11px;line-height:1.4">${pAction}</td>
            <td style="font-size:11px;text-align:center;width:110px">${pResp}</td>
            <td style="font-size:11px;text-align:center;width:90px;${echStyle}">${echeanceFmt}${echSuffix}</td>
            <td style="text-align:center;width:90px">
              <span style="background:${statutBg};color:${statutColor};padding:2px 9px;border-radius:10px;font-size:10px;font-weight:700">${statutLabel}</span>
            </td>
          </tr>`;
        })
        .join("");

      return `<tr style="background:${levelBg}">
          <td colspan="6" style="padding:8px 12px;border-top:2px solid #e8edf5">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              <span style="font-weight:700;color:#0A3475;font-size:12px">${rCode}</span>
              <span style="font-weight:600;font-size:12px;color:#222">${rTitre}</span>
              ${lvBadge}${riSpan}
              <span style="color:#888;font-size:10px;margin-left:auto">${nbPlans} plan${plansPl}</span>
            </div>
          </td>
        </tr>
        ${planRows}
        <tr><td colspan="6" style="padding:3px 0;background:#fff"></td></tr>`;
    })
    .join("");

  // ── Pré-calcul snippets template ───────────────────────────────────────────
  const realisePl = realises > 1 ? "s" : "";
  const risquesPl = risquesAvecPlans.length > 1 ? "s" : "";
  const nbRisques = risquesAvecPlans.length;
  const enRetardSnippet =
    enRetard > 0
      ? ` · <span style="color:#c62828;font-weight:600">${enRetard} en retard</span>`
      : "";
  const enRetardKpi =
    enRetard > 0
      ? `<div class="kpi err"><div class="v">${enRetard}</div><div class="l">En retard ⚠</div></div>`
      : `<div class="kpi dim"><div class="v">0</div><div class="l">En retard</div></div>`;
  const tableOrEmpty =
    total === 0
      ? `<p style="color:#999;font-style:italic;padding:16px 0">Aucun plan d'action enregistré.</p>`
      : `<table>
  <thead><tr>
    <th style="width:28px">#</th>
    <th style="width:100px">Exigence (réf.)</th>
    <th>Action</th>
    <th style="width:110px;text-align:center">Responsable</th>
    <th style="width:90px;text-align:center">Échéance</th>
    <th style="width:90px;text-align:center">Statut</th>
  </tr></thead>
  <tbody>${risqueRows}</tbody>
</table>`;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Suivi plans d'action - ${cartoTitre}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;display:flex;gap:8px;align-items:center;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}

  /* HEADER */
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eyebrow{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:30px;font-weight:700;line-height:1;color:${hdrBadgeColor}}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}

  /* KPI STRIP */
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8}
  .kpi{flex:1;padding:14px 16px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:22px;font-weight:700}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.ok .v{color:#2e7d32}
  .kpi.warn .v{color:#e65100}
  .kpi.err .v{color:#c62828}
  .kpi.blue .v{color:#1565c0}
  .kpi.dim .v{color:#9e9e9e}
  .kpi.neu .v{color:#0A3475}

  /* TAUX BAR */
  .taux-box{background:#f0f4ff;border-bottom:1px solid #d0daf5;padding:14px 40px;
    display:flex;align-items:center;gap:0}
  .taux-label{flex:1;font-size:12px;color:#333}
  .taux-label strong{display:block;font-size:13px;color:#0A3475;margin-bottom:2px}
  .taux-bar-wrap{flex:2;margin:0 24px}
  .taux-bar{background:#e0e0e0;height:10px;border-radius:5px;overflow:hidden}
  .taux-fill{height:10px;border-radius:5px;background:${tauxColor}}
  .taux-pct{font-size:26px;font-weight:700;color:${tauxColor};min-width:58px;text-align:right}

  /* CONTENT */
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}

  /* TABLE */
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1a237e;color:#fff;padding:7px 10px;text-align:left;
    font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:7px 10px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:hover td{background:#f8faff}

  /* FOOTER */
  .footer{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  .footer strong{color:#888}

  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,.taux-box,th,.taux-fill,.hdr-badge{
      -webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eyebrow">${cartoInst} · Rapport officiel</div>
  <h1>Suivi des Plans d'Action - Conformité</h1>
  <div class="hdr-sub">${cartoTitre}</div>
  <div class="hdr-date">Généré le ${esc(dateFmt)}</div>
  <div class="hdr-badge">
    <div class="big">${tauxRealisation}%</div>
    <div class="small">Taux de<br>réalisation</div>
  </div>
</div>

<div class="taux-box">
  <div class="taux-label">
    <strong>Avancement global des plans</strong>
    ${realises} plan${realisePl} réalisé${realisePl} sur ${total}${enRetardSnippet}
  </div>
  <div class="taux-bar-wrap">
    <div class="taux-bar"><div class="taux-fill" style="width:${tauxRealisation}%"></div></div>
    <div style="display:flex;justify-content:space-between;font-size:10px;color:#aaa;margin-top:3px">
      <span>0%</span><span>50%</span><span>100%</span>
    </div>
  </div>
  <div class="taux-pct">${tauxRealisation}%</div>
</div>

<div class="kpi-strip">
  <div class="kpi neu"><div class="v">${total}</div><div class="l">Total plans</div></div>
  <div class="kpi blue"><div class="v">${enCours}</div><div class="l">En cours</div></div>
  <div class="kpi ok"><div class="v">${realises}</div><div class="l">Réalisés</div></div>
  <div class="kpi warn"><div class="v">${reportes}</div><div class="l">Reportés</div></div>
  ${enRetardKpi}
  <div class="kpi neu"><div class="v">${nbRisques}</div><div class="l">Risques concernés</div></div>
</div>

<div class="content">
  <h2>Détail par risque - ${risquesAvecPlans.length} risque${risquesPl} avec plan${risquesPl}</h2>
  ${tableOrEmpty}
</div>

<div class="footer">
  <span>Document confidentiel - usage interne uniquement</span>
  <strong>${cartoInst} · ${esc(dateFmt)}</strong>
</div>
</body>
</html>`;
}
